// -----------------------------------------------------------------------------
// Purpose:
// - Backoffice workflow page for reviewing an open Tender context.
// - Supports two workflow modes:
//      1) quotation: operational review while the Tender or TenderPhase is still in quotation
//      2) analysis: operational review while the Tender or TenderPhase is in analysis
// - Loads Tender data, phases, carriers, lanes, quotes, summaries, analysis-run context,
//   and imported analysis solutions for the current target.
// - Supports inline views depending on the current workflow mode:
//      1) inQuotation overview
//      2) carrier detail
//      3) accepted-by-lane
//      4) total-by-lane
//      5) summary view
// - Persists only lightweight UI state per tab so users can switch tabs without
//   losing navigation context.
// - Reloads heavy datasets from the backend when the tab is reconstructed.
//
// Important architectural rules preserved here:
// - FE must not keep large generic datasets and then do heavy filtering in real time.
// - When the selected Tender / TenderPhase changes, the page reloads the target-scoped
//   backend collections again.
// - For lane views in Non-Spot mode:
//      * the Phase select controls the SAME global selectedPhaseId state already used
//        by the page;
//      * that Phase select is used mainly to drive the current solution target;
//      * the lane tables themselves still receive Tender-wide quote sets and must not
//        phase-filter them on the FE.
// -----------------------------------------------------------------------------

import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useContext,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  LoadingContext,
  MHeader,
  MButton,
  BufferService,
  MMessageBoard,
  M_StateActions,
  NavigateContext,
  MSelect,
} from "src/M_Components";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { isFailurePayload, apiMessage } from "src/components/utils/apiResponse";

import InQuotationForm from "../../components/Tenders/InQuotationForm";
import CarrierResponsesTable from "../../components/Tenders/carrierResponsesTable";
import TenderQuotesAnalysis from "../../components/Tenders/TenderQuotesAnalysis";
import CarrierResponseDetailTable from "../../components/Tenders/CarrierResponseDetailTable";
import ViewLaneTable from "../../components/Tenders/ViewLaneTable";
import TenderSummaryTable from "../../components/Tenders/TenderSummaryTable";
import CarrierInterestsConfig from "../../components/Tenders/CarrierInterestsConfig";

import "./TenderOpenPage.css";

//region Constants

/**
 * Analysis target enum values used by the API.
 */
const ANALYSIS_TARGET_TYPE = {
  Tender: 1,
  TenderPhase: 2,
};

/**
 * Analysis run status enum values used by the API.
 */
const ANALYSIS_RUN_STATUS = {
  PendingExport: 1,
  ExportRunning: 2,
  ExportedAwaitingImport: 3,
  ImportRunning: 4,
  Imported: 5,
  Failed: 6,
  Cancelled: 7,
};

/**
 * External publication target type values returned by the API.
 */
const EXTERNAL_PUBLICATION_TARGET_TYPE = {
  Tender: 1,
  TenderPhase: 2,
};

/**
 * Canonical provider key used to detect enabled OpenSyllo account integrations.
 */
const OPEN_SYLLO_PROVIDER_KEY = "opensyllo";

const QUOTES_PAGE_SIZE = 200;
const LANES_PAGE_SIZE_FLAT = 200;
const SUMMARY_PAGE_SIZE = 200;
const ALL_LANES_PAGE_SIZE = 200;
const DISTINCT_CARRIERS_PAGE_SIZE = 200;
const ANALYSIS_SOLUTIONS_PAGE_SIZE = 200;

//endregion

//region Helpers

/**
 * Builds the namespaced Redux/session key for the current tab.
 *
 * @param {string|number|null|undefined} tid - Tab identifier.
 * @returns {string|null} Namespaced key or null when the id is missing.
 */
const stateKeyFrom = (tid) => (tid ? `${tid}::TOP` : null);

/**
 * Reads a value from a key-value array by key.
 *
 * @param {Array<{key:string,value:any}>} arr - Snapshot array.
 * @param {string} k - Key to retrieve.
 * @returns {any} Stored value or undefined when not found.
 */
const pickKV = (arr, k) => (arr || []).find((x) => x.key === k)?.value;

/**
 * Normalizes common API envelope shapes to a flat array.
 *
 * @param {any} data - API response payload.
 * @returns {Array|any} Normalized array or original object fallback.
 */
const norm = (data) =>
  Array.isArray(data?.Items)
    ? data.Items
    : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.Data)
        ? data.Data
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
            ? data
            : data || [];

/**
 * Performs a defensive deep comparison for snapshot persistence.
 *
 * @param {any} a - First value.
 * @param {any} b - Second value.
 * @returns {boolean} True when values are equivalent.
 */
const deepEq = (a, b) => {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return a === b;
  }
};

/**
 * Converts a value to a finite positive number when possible.
 *
 * @param {any} value - Raw value.
 * @returns {number|null} Positive numeric value or null.
 */
const toPositiveNumberOrNull = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Extracts the most reliable HTTP status code from normalized response shapes.
 *
 * @param {any} value - Raw payload or error object.
 * @returns {number|null} Resolved HTTP status code or null when unavailable.
 */
const getPayloadStatus = (value) => {
  const status =
    value?.__status ??
    value?.status ??
    value?.statusCode ??
    value?.StatusCode ??
    value?.response?.status;

  return typeof status === "number" ? status : null;
};

/**
 * Decodes a base64url-encoded string into UTF-8 text.
 *
 * @param {string} str - Base64url string to decode.
 * @returns {string} Decoded text or empty string when decoding fails.
 */
const base64UrlDecode = (str) => {
  try {
    const pad = (s) => s + "=".repeat((4 - (s.length % 4)) % 4);
    const b64 = pad(String(str || "").replace(/-/g, "+").replace(/_/g, "/"));

    return decodeURIComponent(
      atob(b64)
        .split("")
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join("")
    );
  } catch {
    return "";
  }
};

/**
 * Parses a JWT payload from a token string.
 *
 * @param {string|null|undefined} token - JWT token.
 * @returns {object} Parsed payload or an empty object.
 */
const getJwtPayload = (token) => {
  if (!token) return {};

  const parts = String(token).split(".");
  if (parts.length < 2) return {};

  const json = base64UrlDecode(parts[1]);

  try {
    return JSON.parse(json || "{}");
  } catch {
    return {};
  }
};

/**
 * Reads the current default account id from the JWT claim used by the backend.
 *
 * @returns {number|null} Default account id or null when unavailable.
 */
const getCurrentAccountIdFromToken = () => {
  const token = localStorage.getItem("token");
  const payload = getJwtPayload(token);

  return toPositiveNumberOrNull(payload?.acc);
};

/**
 * Normalizes a provider display name into a stable comparison key.
 *
 * @param {string|null|undefined} provider - Provider label from the API.
 * @returns {string} Normalized provider key.
 */
const normalizeProviderKey = (provider) =>
  String(provider || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

/**
 * Determines whether an account integration represents an enabled OpenSyllo integration.
 *
 * @param {object} integration - Account integration DTO.
 * @returns {boolean} True when the integration is enabled for OpenSyllo.
 */
const isEnabledOpenSylloIntegration = (integration) =>
  normalizeProviderKey(integration?.Provider) === OPEN_SYLLO_PROVIDER_KEY &&
  integration?.IsEnabled === true;

/**
 * Reads the publication id regardless of casing.
 *
 * @param {object|null} publication - External publication DTO.
 * @returns {number|null} Publication id.
 */
const getExternalPublicationId = (publication) =>
  publication?.Id ?? publication?.id ?? null;

/**
 * Determines whether a publication payload represents an active publication.
 *
 * @param {object|null} publication - External publication DTO.
 * @returns {boolean} True when a publication exists and has not been closed.
 */
const isExternalPublicationActive = (publication) => {
  if (!publication || typeof publication !== "object") return false;

  const publicationId = getExternalPublicationId(publication);
  const closedAt = publication?.ClosedAt ?? publication?.closedAt ?? null;

  return publicationId != null && !closedAt;
};

/**
 * Detects the known not-found payload used by the external publication endpoint.
 *
 * @param {any} payload - API response payload.
 * @returns {boolean} True when the response means the publication does not exist.
 */
const isExternalPublicationNotFoundPayload = (payload) => {
  const status = getPayloadStatus(payload);
  if (status === 404) return true;

  if (typeof payload !== "string") return false;

  const text = payload.trim().toLowerCase();

  return (
    text.includes("publicação externa não encontrada") ||
    text.includes("publicacao externa nao encontrada") ||
    text.includes("external publication not found")
  );
};

/**
 * Builds the external-publication API path segment for a target entity.
 *
 * @param {number|string} targetEntityId - Tender or TenderPhase identifier.
 * @returns {string} Relative endpoint segment used by BufferService.
 */
const buildExternalPublicationSegment = (targetEntityId) =>
  `/${targetEntityId}/external-publication`;

/**
 * Reads TenderPhaseId from a quote payload regardless of casing.
 *
 * @param {object} q - Quote payload.
 * @returns {number|null|undefined} Phase id.
 */
const getQuotePhaseId = (q) => q?.TenderPhaseId ?? q?.tenderPhaseId;

/**
 * Reads QuoteId from a quote payload regardless of casing.
 *
 * @param {object} q - Quote payload.
 * @returns {number|null} Quote id.
 */
const getQuoteId = (q) => q?.QuoteId ?? q?.quoteId ?? q?.Id ?? q?.id ?? null;

/**
 * Reads LaneId from a lane payload regardless of casing.
 *
 * @param {object} lane - Lane payload.
 * @returns {number|null} Lane id.
 */
const getLaneId = (lane) =>
  lane?.LaneId ?? lane?.laneId ?? lane?.Id ?? lane?.id ?? null;

/**
 * Reads the lane origin label from a lane payload regardless of casing.
 *
 * @param {object} lane - Lane payload.
 * @returns {string} Origin label.
 */
const getLaneOrigin = (lane) =>
  lane?.LaneSource ??
  lane?.laneSource ??
  lane?.Origin ??
  lane?.origin ??
  lane?.sourceLocation ??
  lane?.SourceLocation ??
  "";

/**
 * Reads the lane destination label from a lane payload regardless of casing.
 *
 * @param {object} lane - Lane payload.
 * @returns {string} Destination label.
 */
const getLaneDestination = (lane) =>
  lane?.LaneDestination ??
  lane?.laneDestination ??
  lane?.Destination ??
  lane?.destination ??
  lane?.destinationLocation ??
  lane?.DestinationLocation ??
  "";

/**
 * Reads the satisfied phase id from a summary payload regardless of casing.
 *
 * @param {object} row - Summary row payload.
 * @returns {number|null} Satisfied phase id.
 */
const getSummarySatisfiedPhaseId = (row) =>
  row?.satisfiedInTenderPhaseId ?? row?.SatisfiedInTenderPhaseId ?? null;

/**
 * Reads the satisfied timestamp from a summary payload regardless of casing.
 *
 * @param {object} row - Summary row payload.
 * @returns {string|null} Satisfied timestamp.
 */
const getSummarySatisfiedAt = (row) =>
  row?.satisfiedAt ?? row?.SatisfiedAt ?? null;

/**
 * Builds the sessionStorage key for a namespaced snapshot.
 *
 * @param {string} ns - Namespace key.
 * @returns {string} Session storage key.
 */
const ssKey = (ns) => `NS_SNAPSHOT:${ns}`;

/**
 * Reads a persisted namespace snapshot from sessionStorage.
 *
 * @param {string|null} ns - Namespace key.
 * @returns {Array|null} Parsed snapshot array or null when unavailable.
 */
const readFromStorage = (ns) => {
  if (!ns) return null;

  try {
    const raw = sessionStorage.getItem(ssKey(ns));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Persists a namespace snapshot into sessionStorage.
 *
 * @param {string|null} ns - Namespace key.
 * @param {Array} snapshotArray - Snapshot entries.
 * @returns {void}
 */
const writeToStorage = (ns, snapshotArray) => {
  if (!ns) return;

  try {
    sessionStorage.setItem(
      ssKey(ns),
      JSON.stringify(Array.isArray(snapshotArray) ? snapshotArray : [])
    );
  } catch {
    // Storage can be unavailable or full. Redux remains the primary source of truth.
  }
};

/**
 * Resolves the real last phase id from the phase chain.
 *
 * @param {Array} phasesList - Tender phases.
 * @returns {number|null} Last phase id.
 */
const resolveLastPhaseId = (phasesList) => {
  const list = Array.isArray(phasesList) ? phasesList : [];
  if (!list.length) return null;

  const lastIds = list
    .map((p) => p?.LastTenderPhaseId ?? p?.lastTenderPhaseId ?? null)
    .filter((x) => x != null);

  const last =
    list.find((p) => !lastIds.includes(p.Id)) || list[list.length - 1];

  return last?.Id ?? null;
};

/**
 * Flattens the last-phase flag onto quotes so downstream components do not need
 * to rely on nested TenderPhase payloads.
 *
 * @param {Array} quotesList - Quote list.
 * @param {number|null} lastPhaseId - Real last phase id.
 * @returns {Array} Enriched quotes.
 */
const withIsLastPhase = (quotesList, lastPhaseId) => {
  const list = Array.isArray(quotesList) ? quotesList : [];
  if (!lastPhaseId) return list;

  return list.map((q) => {
    const qPhaseId = q?.TenderPhaseId ?? q?.tenderPhaseId ?? null;
    const isLast = qPhaseId != null && Number(qPhaseId) === Number(lastPhaseId);

    return {
      ...q,
      IsLastPhase: isLast,
      isLastPhase: isLast,
    };
  });
};

/**
 * Reads SolutionId from a solution payload regardless of casing.
 *
 * @param {object} solution - Solution payload.
 * @returns {number|null} Solution id.
 */
const getSolutionId = (solution) =>
  solution?.Id ??
  solution?.id ??
  solution?.SolutionId ??
  solution?.solutionId ??
  null;

/**
 * Reads SolutionSequence from a solution payload regardless of casing.
 *
 * @param {object} solution - Solution payload.
 * @returns {number|null} Solution sequence.
 */
const getSolutionSequence = (solution) =>
  solution?.SolutionSequence ?? solution?.solutionSequence ?? null;

/**
 * Reads Solution awards regardless of casing.
 *
 * @param {object} solution - Solution payload.
 * @returns {Array} Suggested awards.
 */
const getSolutionAwards = (solution) =>
  Array.isArray(solution?.SuggestedAwards)
    ? solution.SuggestedAwards
    : Array.isArray(solution?.suggestedAwards)
      ? solution.suggestedAwards
      : [];

/**
 * Reads TenderQuoteId from a suggested-award payload regardless of casing.
 *
 * @param {object} award - Suggested-award payload.
 * @returns {number|null} Quote id.
 */
const getAwardQuoteId = (award) =>
  award?.TenderQuoteId ?? award?.tenderQuoteId ?? null;

/**
 * Reads CarrierId from a suggested-award payload regardless of casing.
 *
 * @param {object} award - Suggested-award payload.
 * @returns {number|null} Carrier id.
 */
const getAwardCarrierId = (award) =>
  award?.CarrierId ?? award?.carrierId ?? null;

/**
 * Reads LaneId from a suggested-award payload regardless of casing.
 *
 * @param {object} award - Suggested-award payload.
 * @returns {number|null} Lane id.
 */
const getAwardLaneId = (award) =>
  award?.LaneId ?? award?.laneId ?? null;

/**
 * Builds a lane-best map from summary rows.
 *
 * Important:
 * - now keeps BOTH commercial baselines separately:
 *   - bestUnitValue / bestUnitPrice / minUnitPrice
 *   - bestBidValue  / minBidValue
 * - also keeps loads and ETD baselines;
 * - this allows downstream tables to compare using the currently selected metric
 *   without overloading one field with another semantic.
 *
 * @param {Array} rows - Summary rows from the BE.
 * @returns {Map<number, object>} Lane best metric map.
 */
const buildLaneBestMapFromSummaryRows = (rows) => {
  const map = new Map();

  const toNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  for (const row of Array.isArray(rows) ? rows : []) {
    const laneIdRaw = row?.LaneId ?? row?.laneId ?? row?.Id ?? row?.id ?? null;
    const laneId = laneIdRaw != null ? Number(laneIdRaw) : null;
    if (!Number.isFinite(laneId)) continue;

    const current = map.get(laneId) || {
      bestUnitValue: null,
      bestUnitPrice: null,
      minUnitPrice: null,

      bestBidValue: null,
      minBidValue: null,

      bestLoadsValue: null,
      maxLoads: null,

      bestEtdValue: null,
      minEstimatedTransitDays: null,
    };

    const minUnitPrice = toNumber(
      row?.MinUnitPrice ?? row?.minUnitPrice
    );

    const minBidValue = toNumber(
      row?.MinBidValue ?? row?.minBidValue
    );

    const maxLoads = toNumber(
      row?.MaxLoads ?? row?.maxLoads
    );

    const minEtd = toNumber(
      row?.MinEstimatedTransitDays ?? row?.minEstimatedTransitDays
    );

    if (minUnitPrice != null) {
      const nextUnit =
        current.bestUnitValue == null
          ? minUnitPrice
          : Math.min(current.bestUnitValue, minUnitPrice);

      current.bestUnitValue = nextUnit;
      current.bestUnitPrice = nextUnit;
      current.minUnitPrice = nextUnit;
    }

    if (minBidValue != null) {
      const nextBid =
        current.bestBidValue == null
          ? minBidValue
          : Math.min(current.bestBidValue, minBidValue);

      current.bestBidValue = nextBid;
      current.minBidValue = nextBid;
    }

    if (maxLoads != null) {
      const nextLoads =
        current.bestLoadsValue == null
          ? maxLoads
          : Math.max(current.bestLoadsValue, maxLoads);

      current.bestLoadsValue = nextLoads;
      current.maxLoads = nextLoads;
    }

    if (minEtd != null) {
      const nextEtd =
        current.bestEtdValue == null
          ? minEtd
          : Math.min(current.bestEtdValue, minEtd);

      current.bestEtdValue = nextEtd;
      current.minEstimatedTransitDays = nextEtd;
    }

    map.set(laneId, current);
  }

  return map;
};

/**
 * Builds quote-derived lane metrics.
 *
 * Important:
 * - used as a defensive fallback for metrics that are not reliably aggregated
 *   by the current summary endpoint;
 * - when the caller passes accepted quotes only, the resulting baselines keep
 *   accepted-scope semantics.
 *
 * @param {Array} quotesList - Quote rows.
 * @returns {Map<number, object>} Quote-derived lane metrics.
 */
const buildQuoteMetricsByLane = (quotesList) => {
  const map = new Map();

  const toNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  for (const quote of Array.isArray(quotesList) ? quotesList : []) {
    const laneIdRaw = quote?.LaneId ?? quote?.laneId ?? null;
    const laneId = laneIdRaw != null ? Number(laneIdRaw) : null;
    if (!Number.isFinite(laneId)) continue;

    const current = map.get(laneId) || {
      minUnitPrice: null,
      minBidValue: null,
      maxLoads: null,
      minEstimatedTransitDays: null,
    };

    const unitPrice = toNumber(quote?.UnitPrice ?? quote?.unitPrice);
    const bidValue = toNumber(quote?.BidValue ?? quote?.bidValue);
    const loads = toNumber(quote?.Loads ?? quote?.loads);
    const etd = toNumber(
      quote?.EstimatedTransitDays ?? quote?.estimatedTransitDays
    );

    if (unitPrice != null) {
      current.minUnitPrice =
        current.minUnitPrice == null
          ? unitPrice
          : Math.min(current.minUnitPrice, unitPrice);
    }

    if (bidValue != null) {
      current.minBidValue =
        current.minBidValue == null
          ? bidValue
          : Math.min(current.minBidValue, bidValue);
    }

    if (loads != null) {
      current.maxLoads =
        current.maxLoads == null ? loads : Math.max(current.maxLoads, loads);
    }

    if (etd != null) {
      current.minEstimatedTransitDays =
        current.minEstimatedTransitDays == null
          ? etd
          : Math.min(current.minEstimatedTransitDays, etd);
    }

    map.set(laneId, current);
  }

  return map;
};

/**
 * Builds a lane -> remaining loads map.
 *
 * Rule:
 * - remaining loads = Tender.Loads - Summary.TotalLoads for that lane
 *
 * Important:
 * - clamps the result to zero;
 * - uses the same concept already applied in TenderSummary.
 *
 * @param {object} args - Builder args.
 * @param {Array} args.lanes - Tender-wide lane universe.
 * @param {Array} args.summaryRows - Summary rows for the target table mode.
 * @param {number|string|null} args.tenderLoads - Tender total loads target.
 * @returns {Map<number, number>} Remaining loads per lane.
 */
const buildRemainingLoadsByLaneMap = ({ lanes, summaryRows, tenderLoads }) => {
  const map = new Map();

  const normalizedTenderLoads = Number(tenderLoads);
  const targetLoads = Number.isFinite(normalizedTenderLoads)
    ? normalizedTenderLoads
    : 0;

  const summaryByLaneId = new Map();

  for (const row of Array.isArray(summaryRows) ? summaryRows : []) {
    const laneIdRaw = row?.LaneId ?? row?.laneId ?? row?.Id ?? row?.id ?? null;
    const laneId = laneIdRaw != null ? Number(laneIdRaw) : null;
    if (!Number.isFinite(laneId)) continue;

    summaryByLaneId.set(laneId, row);
  }

  for (const lane of Array.isArray(lanes) ? lanes : []) {
    const laneIdRaw =
      lane?.LaneId ?? lane?.laneId ?? lane?.Id ?? lane?.id ?? null;
    const laneId = laneIdRaw != null ? Number(laneIdRaw) : null;
    if (!Number.isFinite(laneId)) continue;

    const summary = summaryByLaneId.get(laneId) || null;
    const totalLoadsRaw = Number(summary?.TotalLoads ?? summary?.totalLoads);
    const totalLoads = Number.isFinite(totalLoadsRaw) ? totalLoadsRaw : 0;

    map.set(laneId, Math.max(0, targetLoads - totalLoads));
  }

  return map;
};

/**
 * Reads solution display name regardless of casing.
 *
 * @param {object} solution - Solution payload.
 * @returns {string|null} Display name.
 */
const getSolutionDisplayName = (solution) => {
  const sequence = getSolutionSequence(solution);

  return (
    solution?.DisplayName ??
    solution?.displayName ??
    (sequence != null ? `Solution ${sequence}` : null)
  );
};

/**
 * Reads solution total cost regardless of casing.
 *
 * @param {object} solution - Solution payload.
 * @returns {number|null} Total cost.
 */
const getSolutionTotalCost = (solution) =>
  solution?.TotalCost ?? solution?.totalCost ?? null;

/**
 * Reads solution assigned loads regardless of casing.
 *
 * @param {object} solution - Solution payload.
 * @returns {number|null} Assigned loads.
 */
const getSolutionAssignedLoads = (solution) =>
  solution?.AssignedLoads ?? solution?.assignedLoads ?? null;

/**
 * Reads solution possible loads regardless of casing.
 *
 * @param {object} solution - Solution payload.
 * @returns {number|null} Possible loads.
 */
const getSolutionPossibleLoads = (solution) =>
  solution?.PossibleLoads ?? solution?.possibleLoads ?? null;

/**
 * Reads the Tender currency value regardless of casing/shape.
 *
 * Important:
 * - the selected analysis solution belongs to a Tender or TenderPhase;
 * - even in TenderPhase mode, the currency belongs to the parent Tender;
 * - this helper keeps the banner resilient to different API shapes.
 *
 * Supported shapes:
 * - tender.Currency = "$"
 * - tender.currency = "EUR"
 * - tender.CurrencyCode = "USD"
 * - tender.Currency = { Symbol, Code, Name }
 *
 * @param {object} tender - Tender payload.
 * @returns {string} Printable currency suffix.
 */
const getTenderCurrencyLabel = (tender) => {
  const rawCurrency =
    tender?.Currency ??
    tender?.currency ??
    tender?.CurrencySymbol ??
    tender?.currencySymbol ??
    tender?.CurrencyCode ??
    tender?.currencyCode ??
    tender?.CurrencyName ??
    tender?.currencyName ??
    "";

  if (rawCurrency && typeof rawCurrency === "object") {
    return String(
      rawCurrency.Symbol ??
      rawCurrency.symbol ??
      rawCurrency.Code ??
      rawCurrency.code ??
      rawCurrency.Name ??
      rawCurrency.name ??
      ""
    ).trim();
  }

  return String(rawCurrency ?? "").trim();
};

/**
 * Maps banner tone values to CSS modifier classes.
 *
 * @param {"success"|"error"|"warning"|"info"|"neutral"} tone - Banner tone.
 * @returns {string} CSS class name.
 */
const getAnalysisBannerToneClass = (tone) => {
  switch (tone) {
    case "success":
      return "tiq-analysis-status-banner--success";
    case "error":
      return "tiq-analysis-status-banner--error";
    case "warning":
      return "tiq-analysis-status-banner--warning";
    case "neutral":
      return "tiq-analysis-status-banner--neutral";
    default:
      return "tiq-analysis-status-banner--info";
  }
};

/**
 * Normalizes a TenderCarrier, TenderPhaseCarrier, or legacy carrier row into
 * the carrier shape consumed by this page.
 *
 * Important:
 * - association endpoints expose their own Id values;
 * - child components must compare quotes against the real CarrierId;
 * - legacy phase-carrier endpoints can still return the carrier itself.
 *
 * @param {object} row - Raw carrier or carrier association payload.
 * @param {number|string|null} phaseId - Phase scope provided by the caller.
 * @returns {object|null} Normalized carrier row or null when no carrier id exists.
 */
const normalizeInvitedCarrierRow = (row, phaseId = null) => {
  if (!row || typeof row !== "object") return null;

  const carrier = row?.Carrier ?? row?.carrier ?? {};
  const rowPhaseId =
    row?.TenderPhaseId ?? row?.tenderPhaseId ?? phaseId ?? null;

  const carrierId =
    row?.CarrierId ??
    row?.carrierId ??
    carrier?.CarrierId ??
    carrier?.carrierId ??
    carrier?.Id ??
    carrier?.id ??
    (row?.Carrier == null && row?.carrier == null ? row?.Id ?? row?.id : null);

  if (carrierId == null) return null;

  const carrierName =
    carrier?.CarrierName ??
    carrier?.carrierName ??
    carrier?.Name ??
    carrier?.name ??
    row?.CarrierName ??
    row?.carrierName ??
    row?.Name ??
    row?.name ??
    "";

  return {
    ...carrier,
    Id: carrierId,
    id: carrier?.id ?? carrierId,
    CarrierId: carrierId,
    carrierId,
    CarrierName: carrierName,
    carrierName: carrier?.carrierName ?? carrierName,
    Name: carrierName,
    name: carrier?.name ?? carrierName,
    TenderCarrierId:
      row?.TenderId != null || row?.tenderId != null
        ? row?.Id ?? row?.id ?? null
        : null,
    TenderPhaseCarrierId:
      rowPhaseId != null ? row?.Id ?? row?.id ?? null : null,
    TenderId: row?.TenderId ?? row?.tenderId ?? null,
    tenderId: row?.tenderId ?? row?.TenderId ?? null,
    TenderPhaseId: rowPhaseId,
    tenderPhaseId: rowPhaseId,
    HasSubmitted: Boolean(row?.HasSubmitted ?? row?.hasSubmitted ?? false),
    hasSubmitted: Boolean(row?.HasSubmitted ?? row?.hasSubmitted ?? false),
    SubmittedAt: row?.SubmittedAt ?? row?.submittedAt ?? null,
    submittedAt: row?.submittedAt ?? row?.SubmittedAt ?? null,
    InvitedPhaseIds: rowPhaseId != null ? [rowPhaseId] : [],
    invitedPhaseIds: rowPhaseId != null ? [rowPhaseId] : [],
  };
};

/**
 * Merges invited carrier rows by real CarrierId while preserving phase scope.
 *
 * Non-Spot tenders can invite the same carrier in multiple phases. Keeping one
 * row per carrier avoids duplicated selector/table rows while preserving the
 * phase list needed to show phase-specific participation.
 *
 * @param {Array<object>} rows - Normalized invited-carrier rows.
 * @returns {Array<object>} Unique carriers with accumulated phase ids.
 */
const mergeInvitedCarriers = (rows) => {
  const map = new Map();

  for (const row of Array.isArray(rows) ? rows : []) {
    const carrierId = row?.CarrierId ?? row?.carrierId ?? row?.Id ?? row?.id;
    if (carrierId == null) continue;

    const key = String(carrierId);
    const existing = map.get(key);
    const currentPhaseIds = Array.isArray(row?.InvitedPhaseIds)
      ? row.InvitedPhaseIds
      : [];

    if (!existing) {
      const uniquePhaseIds = [...new Set(currentPhaseIds)];
      map.set(key, {
        ...row,
        InvitedPhaseIds: uniquePhaseIds,
        invitedPhaseIds: uniquePhaseIds,
      });
      continue;
    }

    const mergedPhaseIds = [
      ...new Set([
        ...(existing.InvitedPhaseIds || []),
        ...currentPhaseIds,
      ]),
    ];

    map.set(key, {
      ...existing,
      ...row,
      Id: existing.Id ?? row.Id,
      id: existing.id ?? row.id,
      CarrierId: existing.CarrierId ?? row.CarrierId,
      carrierId: existing.carrierId ?? row.carrierId,
      CarrierName: existing.CarrierName || row.CarrierName || "",
      carrierName: existing.carrierName || row.carrierName || "",
      Name: existing.Name || row.Name || "",
      name: existing.name || row.name || "",
      HasSubmitted: Boolean(existing.HasSubmitted || row.HasSubmitted),
      hasSubmitted: Boolean(existing.hasSubmitted || row.hasSubmitted),
      SubmittedAt: row.SubmittedAt ?? existing.SubmittedAt ?? null,
      submittedAt: row.submittedAt ?? existing.submittedAt ?? null,
      InvitedPhaseIds: mergedPhaseIds,
      invitedPhaseIds: mergedPhaseIds,
    });
  }

  return Array.from(map.values());
};

/**
 * Determines whether a carrier belongs to a selected TenderPhase.
 *
 * Rows without phase metadata remain visible for compatibility with legacy
 * tender-wide carrier payloads.
 *
 * @param {object} carrier - Carrier row used by this page.
 * @param {number|string|null} phaseId - Selected phase id.
 * @returns {boolean} True when the carrier can be shown for the phase.
 */
const isCarrierInvitedInPhase = (carrier, phaseId) => {
  if (phaseId == null) return false;

  const phaseIds =
    carrier?.InvitedPhaseIds ??
    carrier?.invitedPhaseIds ??
    carrier?.TenderPhaseIds ??
    carrier?.tenderPhaseIds ??
    [];

  if (!Array.isArray(phaseIds) || phaseIds.length === 0) return true;

  return phaseIds.some((id) => String(id) === String(phaseId));
};

/**
 * Builds the lightweight option shape used by the carrier selector.
 *
 * @param {object} carrier - Carrier row used by this page.
 * @returns {{label:string,value:number|string}|null} Select option or null.
 */
const buildCarrierSelectOption = (carrier) => {
  const carrierId =
    carrier?.CarrierId ?? carrier?.carrierId ?? carrier?.Id ?? carrier?.id ?? null;

  if (carrierId == null) return null;

  return {
    label: carrier?.CarrierName ?? carrier?.Name ?? carrier?.name ?? "",
    value: carrierId,
  };
};

//endregion

//region Snapshot keys

/**
 * Stores only lightweight UI state and lightweight data needed to reconstruct
 * the page navigation context.
 *
 * Heavy datasets such as quotes, lanes, summaries and solutions are reloaded
 * from the backend when the tab is rebuilt.
 */
const K = {
  owner: "TOP.ownerTabId",
  operation: "TOP.operation",
  sessionLine: "TOP.sessionLine",
  activeView: "TOP.activeView",
  selectedCarrierId: "TOP.selectedCarrierId",
  selectedPhaseId: "TOP.selectedPhaseId",
  detailPhaseId: "TOP.detailPhaseId",
  selectedSolutionId: "TOP.selectedSolutionId",
  bestMetric: "TOP.bestMetric",
  tenderInfo: "TOP.tenderInfo",
  phases: "TOP.phases",
  lastSyncAt: "TOP.lastSyncAt",
  tenderSchemaOptions: "TOP.tenderSchemaOptions",
  tenderFkData: "TOP.tenderFkData",
};

//endregion

//region Component

/**
 * Page responsible for the open Tender workflow.
 *
 * Responsibilities:
 * - Load Tender context and related datasets.
 * - Orchestrate all inline views.
 * - Keep lightweight per-tab UI state persisted.
 * - Rebuild heavy data from the backend when required.
 * - Resolve the current analysis target and the latest analysis run for that target.
 * - Load imported analysis solutions for the current target when analysis results are ready.
 * - Expose quotation-mode and analysis-mode actions from the same page component.
 *
 * Important behavioral notes:
 * - selectedPhaseId is the single global phase source of truth for this page;
 * - Carrier view and lane views share that same phase state;
 * - in lane views, the phase selector mainly changes the solution target, not the
 *   Tender-wide quote set rendered by the tables;
 * - large collections are reloaded from the backend instead of being broadly
 *   filtered client-side.
 *
 * @param {object} props - Component props.
 * @param {Function} props.handleTabClose - Callback used to close the current tab.
 * @param {Function} props.toastMessages - Optional toast dispatcher override.
 * @param {"quotation"|"analysis"} props.workflowMode - Current workflow mode.
 * @returns {JSX.Element} Page component.
 */
export default function TenderOpenPage({
  handleTabClose,
  toastMessages,
  workflowMode = "analysis",
}) {
  const { t, i18n } = useTranslation();
  const ctx = useContext(LoadingContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { renameTab } = useContext(NavigateContext);

  //region Redux state

  const selectedTab = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === "selectedTab")?.value
  );
  const tabsList = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === "tabs")?.value
  );
  const globalOperation = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === "operation")?.value
  );
  const globalLine = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === "TenderLine")?.value
  );
  const stateKeyValue = useSelector((s) => s.StateKeyValue);

  const allStateKeys = useMemo(
    () => (stateKeyValue || []).map((o) => o.key),
    [stateKeyValue]
  );

  const currentNsKey = stateKeyFrom(selectedTab);
  const currentNsSnapshot = useSelector((s) =>
    currentNsKey
      ? s.StateKeyValue.find((o) => o.key === currentNsKey)?.value
      : undefined
  );

  //endregion

  //region Local UI state

  const [id, setId] = useState(selectedTab);

  useEffect(() => {
    setId(selectedTab);
  }, [selectedTab]);

  const [operation, setOperation] = useState(globalOperation ?? "View");
  const [sessionLine, setSessionLine] = useState(globalLine ?? null);

  /**
   * Active view values:
   * - inQuotation
   * - carrier
   * - acceptedByLane
   * - totalByLane
   * - summary
   * - carrierInterests
   */
  const [activeView, setActiveView] = useState("inQuotation");

  const [selectedCarrierId, setSelectedCarrierId] = useState(null);
  const [selectedPhaseId, setSelectedPhaseId] = useState(null);
  const [detailPhaseId, setDetailPhaseId] = useState(null);
  const [selectedSolutionId, setSelectedSolutionId] = useState(null);

  /**
   * Metric values are intentionally kept stable for downstream compatibility:
   * - unit
   * - bid
   * - loads
   * - etd
   *
   */
  const [bestMetric, setBestMetric] = useState("unit");

  const [isFirst, setIsFirst] = useState(true);
  const [lastSyncAt, setLastSyncAt] = useState(null);

  const [showCreatePhaseConfirm, setShowCreatePhaseConfirm] = useState(false);
  const [showFinishConfirm, setShowFinishConfirm] = useState(false);
  const [showMoveToAnalysisConfirm, setShowMoveToAnalysisConfirm] =
    useState(false);
  const [showExternalPublishConfirm, setShowExternalPublishConfirm] =
    useState(false);

  //endregion

  //region Derived workflow flags

  const isAnalysisWorkflow = workflowMode === "analysis";
  const isQuotationWorkflow = workflowMode === "quotation";

  /**
   * In quotation workflow, only the Summary view must remain blocked.
   *
   * AcceptedByLane and TotalByLane are now valid in both workflows.
   */
  useEffect(() => {
    if (!isAnalysisWorkflow && activeView === "summary") {
      setActiveView("inQuotation");
    }
  }, [activeView, isAnalysisWorkflow]);

  //endregion

  //region Toast and loading refs

  const toastRef = useRef(toastMessages || ctx.toastMessages);
  const setLoadRef = useRef(ctx.setIsLoad);
  const loadCountRef = useRef(0);
  const lastSyncAtRef = useRef(null);

  useEffect(() => {
    setLoadRef.current = ctx.setIsLoad;
  }, [ctx.setIsLoad]);

  useEffect(() => {
    toastRef.current = toastMessages || ctx.toastMessages;
  }, [toastMessages, ctx.toastMessages]);

  useEffect(() => {
    lastSyncAtRef.current = lastSyncAt;
  }, [lastSyncAt]);

  /**
   * Increments the global page loading counter.
   *
   * @returns {void}
   */
  const incLoad = useCallback(() => {
    const prev = loadCountRef.current;
    const next = prev + 1;
    loadCountRef.current = next;

    if (prev === 0) setLoadRef.current?.(true);
  }, []);

  /**
   * Decrements the global page loading counter.
   *
   * @returns {void}
   */
  const decLoad = useCallback(() => {
    const prev = loadCountRef.current;
    const next = Math.max(0, prev - 1);
    loadCountRef.current = next;

    if (prev === 1 && next === 0) setLoadRef.current?.(false);
  }, []);

  /**
   * Returns a translated label when the key exists, or a fallback text when the
   * translation key is not yet registered.
   *
   * @param {string} key - Translation key.
   * @param {string} fallback - Fallback text.
   * @returns {string} Translated or fallback label.
   */
  const tOr = useCallback(
    (key, fallback) => {
      const value = t(key);
      return value && value !== key ? value : fallback;
    },
    [t]
  );

  //endregion

  //region Services

  const baseUrl = ctx.DEFAULTURL;

  const tenderService = useMemo(
    () => new BufferService("Tenders", baseUrl),
    [baseUrl]
  );
  const tenderServiceUpdate = useMemo(
    () => new BufferService("Tenders/update", baseUrl),
    [baseUrl]
  );
  const phasesService = useMemo(
    () => new BufferService("TenderPhases", baseUrl),
    [baseUrl]
  );
  const tenderPhasesUpdateService = useMemo(
    () => new BufferService("TenderPhases/update", baseUrl),
    [baseUrl]
  );
  const tenderQuotesService = useMemo(
    () => new BufferService("TenderQuotes", baseUrl),
    [baseUrl]
  );
  const laneSummariesService = useMemo(
    () => new BufferService("TenderQuotes/laneSummaries", baseUrl),
    [baseUrl]
  );
  const laneSummariesAcceptedService = useMemo(
    () => new BufferService("TenderQuotes/laneSummaries/accepted", baseUrl),
    [baseUrl]
  );
  const distinctCarriersService = useMemo(
    () => new BufferService("TenderQuotes/distinctCarriers", baseUrl),
    [baseUrl]
  );
  const tenderQuotesUpdateService = useMemo(
    () => new BufferService("TenderQuotes/update", baseUrl),
    [baseUrl]
  );
  const tenderLanesFlatService = useMemo(
    () => new BufferService("TenderLanes/flat", baseUrl),
    [baseUrl]
  );
  const tenderLanesSummaryService = useMemo(
    () => new BufferService("TenderLanes", baseUrl),
    [baseUrl]
  );
  const tenderCarriersService = useMemo(
    () => new BufferService("TenderCarriers", baseUrl),
    [baseUrl]
  );
  const tenderPhaseCarriersService = useMemo(
    () => new BufferService("TenderPhaseCarriers", baseUrl),
    [baseUrl]
  );
  const phaseCarriersService = useMemo(
    () => new BufferService("TenderPhases", baseUrl),
    [baseUrl]
  );
  const tenderOptionsService = useMemo(
    () => new BufferService("Tenders", baseUrl),
    [baseUrl]
  );
  const tenderAnalysisRunsService = useMemo(
    () => new BufferService("TenderAnalysis/Runs", baseUrl),
    [baseUrl]
  );
  const tenderAnalysisSolutionsService = useMemo(
    () => new BufferService("TenderAnalysisSolutions", baseUrl),
    [baseUrl]
  );

  const accountIntegrationsService = useMemo(
    () => new BufferService("AccountIntegrations", baseUrl),
    [baseUrl]
  );

  //endregion

  //region Loaded datasets

  const [tenderInfo, setTenderInfo] = useState({});
  const [phases, setPhases] = useState([]);

  const [quotes, setQuotes] = useState([]);
  const [quotesAccepted, setQuotesAccepted] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [quotesUnaccepted, setQuotesUnaccepted] = useState([]);

  const [lanes, setLanes] = useState([]);
  const [allLanes, setAllLanes] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [distinctCarriersItems, setDistinctCarriersItems] = useState([]);
  const [summaryRows, setSummaryRows] = useState([]);

  /**
   * Current-target summaries:
   * - Spot: Tender-wide because there is no phase dimension
   * - Non-Spot: scoped to the current global selectedPhaseId
   *
   * These are used by:
   * - inQuotation chart
   * - carrier detail view
   */
  const [laneSummariesRows, setLaneSummariesRows] = useState([]);

  // eslint-disable-next-line no-unused-vars
  const [laneSummariesAcceptedRows, setLaneSummariesAcceptedRows] = useState(
    []
  );

  /**
   * Tender-wide summaries:
   * - always scoped only by TenderId
   * - never narrowed by selectedPhaseId
   *
   * These are used by:
   * - acceptedByLane
   * - totalByLane
   * - summary
   *
   * This separation is important because lane views are Tender-wide even in
   * Non-Spot mode.
   */
  const [laneSummariesTenderWideRows, setLaneSummariesTenderWideRows] =
    useState([]);
  const [
    laneSummariesAcceptedTenderWideRows,
    setLaneSummariesAcceptedTenderWideRows,
  ] = useState([]);

  /**
   * Current imported solutions for the active target.
   *
   * Important:
   * - Spot: loaded by TenderId
   * - Non-Spot: loaded by the global selectedPhaseId
   *
   * The page intentionally reloads this collection from the backend whenever the
   * target changes instead of loading a large generic solution set and filtering
   * it on the FE.
   */
  const [analysisSolutions, setAnalysisSolutions] = useState([]);

  const [refreshKey, setRefreshKey] = useState(0);
  const [distinctCarriersReloadKey, setDistinctCarriersReloadKey] = useState(0);

  const [tenderSchemaOptions, setTenderSchemaOptions] = useState([]);
  const [tenderFkData, setTenderFkData] = useState({});

  const [acceptBusyIds, setAcceptBusyIds] = useState(() => new Set());
  const acceptBusyIdsRef = useRef(acceptBusyIds);

  const [currentAnalysisRun, setCurrentAnalysisRun] = useState(null);

  /**
   * OpenSyllo integration and publication state.
   */
  const [hasOpenSylloIntegration, setHasOpenSylloIntegration] = useState(false);
  const [openSylloIntegrationChecked, setOpenSylloIntegrationChecked] =
    useState(false);
  const [externalPublication, setExternalPublication] = useState(null);
  const [externalPublicationLoaded, setExternalPublicationLoaded] =
    useState(false);
  const [externalPublicationReloadKey, setExternalPublicationReloadKey] =
    useState(0);

  /**
   * Reads the current accepted flag from a quote regardless of casing.
   *
   * @param {object} q - Quote payload.
   * @returns {boolean} Accepted flag.
   */
  const getQuoteAccepted = (q) => {
    if (!q) return false;
    return Boolean(q.IsAccepted ?? q.isAccepted ?? false);
  };

  useEffect(() => {
    acceptBusyIdsRef.current = acceptBusyIds;
  }, [acceptBusyIds]);

  //endregion

  //region Snapshot infrastructure

  const activeNsRef = useRef(null);
  const activeTidRef = useRef(null);
  const prevTabRef = useRef(null);
  const persistDebounceRef = useRef(null);
  const didGCRef = useRef(false);
  const hydratedOnceRef = useRef(false);
  const nsSnapshotRef = useRef([]);
  const rehydratingRef = useRef(false);

  useEffect(() => {
    if (currentNsKey && activeNsRef.current === currentNsKey) {
      nsSnapshotRef.current = Array.isArray(currentNsSnapshot)
        ? currentNsSnapshot
        : [];
    }
  }, [currentNsKey, currentNsSnapshot]);

  /**
   * Writes a partial set of entries into the current tab namespace.
   *
   * @param {string|null} nsKey - Namespace key.
   * @param {Array<{key:string,value:any}>} entries - Entries to merge.
   * @returns {void}
   */
  const setSession = useCallback(
    (nsKey, entries) => {
      if (!nsKey) return;

      const current =
        nsKey === activeNsRef.current ? nsSnapshotRef.current || [] : [];
      const map = new Map(current.map((e) => [e.key, e]));
      let changed = false;

      for (const kv of entries) {
        const prevVal = map.get(kv.key)?.value;
        if (!deepEq(prevVal, kv.value)) {
          changed = true;
          map.set(kv.key, kv);
        }
      }

      if (changed) {
        const nextArray = Array.from(map.values());
        dispatch(M_StateActions.addM_State({ key: nsKey, value: nextArray }));
        writeToStorage(nsKey, nextArray);

        if (nsKey === activeNsRef.current) nsSnapshotRef.current = nextArray;
      }
    },
    [dispatch]
  );

  /**
   * Persists only lightweight tab state.
   *
   * Heavy collections are intentionally excluded so the snapshot does not grow
   * with thousands of quotes, lanes, summaries or solution payloads.
   *
   * @param {string|number|null} forTabId - Optional explicit tab id.
   * @returns {void}
   */
  const flushSnapshot = useCallback(
    (forTabId = null) => {
      const tid = forTabId ?? activeTidRef.current ?? id;
      const ns = stateKeyFrom(tid);
      if (!ns) return;

      const payload = [
        { key: K.owner, value: String(tid) },
        { key: K.operation, value: operation },
        { key: K.sessionLine, value: sessionLine },
        { key: K.activeView, value: activeView },
        { key: K.selectedCarrierId, value: selectedCarrierId },
        { key: K.selectedPhaseId, value: selectedPhaseId },
        { key: K.detailPhaseId, value: detailPhaseId },
        { key: K.selectedSolutionId, value: selectedSolutionId },
        { key: K.bestMetric, value: bestMetric },
        { key: K.tenderInfo, value: tenderInfo || {} },
        { key: K.phases, value: phases || [] },
        { key: K.lastSyncAt, value: lastSyncAt },
        { key: K.tenderSchemaOptions, value: tenderSchemaOptions || [] },
        { key: K.tenderFkData, value: tenderFkData || {} },
      ];

      setSession(ns, payload);
    },
    [
      id,
      operation,
      sessionLine,
      activeView,
      selectedCarrierId,
      selectedPhaseId,
      detailPhaseId,
      selectedSolutionId,
      bestMetric,
      tenderInfo,
      phases,
      lastSyncAt,
      tenderSchemaOptions,
      tenderFkData,
      setSession,
    ]
  );

  useEffect(() => {
    if (didGCRef.current) return;
    if (!tabsList || tabsList.length === 0) return;

    didGCRef.current = true;

    try {
      const openIds = new Set((tabsList || []).map((tab) => String(tab.value)));

      (allStateKeys || [])
        .filter((key) => typeof key === "string" && key.endsWith("::TOP"))
        .forEach((ns) => {
          const tid = ns.split("::")[0];

          if (!openIds.has(String(tid))) {
            dispatch(M_StateActions.removeM_State({ key: ns }));
            sessionStorage.removeItem(ssKey(ns));
          }
        });
    } catch {
      // No action is required when cleanup fails.
    }
  }, [tabsList, allStateKeys, dispatch]);

  /**
   * Clears heavy in-memory datasets so a restored tab always reconstructs them
   * from the backend instead of reusing stale arrays from a previous tab instance.
   *
   * @returns {void}
   */
  const clearHeavyState = useCallback(() => {
    setQuotes([]);
    setQuotesAccepted([]);
    setQuotesUnaccepted([]);
    setLanes([]);
    setAllLanes([]);
    setCarriers([]);
    setDistinctCarriersItems([]);
    setSummaryRows([]);
    setLaneSummariesRows([]);
    setLaneSummariesAcceptedRows([]);
    setLaneSummariesTenderWideRows([]);
    setLaneSummariesAcceptedTenderWideRows([]);
    setAnalysisSolutions([]);
    setCurrentAnalysisRun(null);

    setHasOpenSylloIntegration(false);
    setOpenSylloIntegrationChecked(false);
    setExternalPublication(null);
    setExternalPublicationLoaded(false);
  }, []);

  useLayoutEffect(() => {
    const prev = prevTabRef.current;
    if (prev !== null && prev !== selectedTab) flushSnapshot(prev);

    if (!selectedTab) {
      prevTabRef.current = null;
      return;
    }

    const ns = stateKeyFrom(selectedTab);
    activeNsRef.current = ns;
    activeTidRef.current = selectedTab;

    let snap = Array.isArray(currentNsSnapshot) ? currentNsSnapshot : [];

    if (!snap.length) {
      const fromStore = readFromStorage(ns);

      if (Array.isArray(fromStore) && fromStore.length) {
        snap = fromStore;
        dispatch(M_StateActions.addM_State({ key: ns, value: snap }));
      }
    }

    rehydratingRef.current = true;

    const storedOwner = pickKV(snap, K.owner);
    const storedOp = pickKV(snap, K.operation);
    const storedLine = pickKV(snap, K.sessionLine);
    const storedView = pickKV(snap, K.activeView);
    const storedCar = pickKV(snap, K.selectedCarrierId);
    const storedPhase = pickKV(snap, K.selectedPhaseId);
    const storedDetPh = pickKV(snap, K.detailPhaseId);
    const storedSolution = pickKV(snap, K.selectedSolutionId);
    const storedBestMetric = pickKV(snap, K.bestMetric);
    const snapTender = pickKV(snap, K.tenderInfo);
    const snapPhases = pickKV(snap, K.phases);
    const snapLastSyncAt = pickKV(snap, K.lastSyncAt);
    const snapTenderSchema = pickKV(snap, K.tenderSchemaOptions);
    const snapTenderFK = pickKV(snap, K.tenderFkData);

    clearHeavyState();

    if (storedOwner) {
      if (storedOp !== undefined) setOperation(storedOp);
      if (storedLine !== undefined) setSessionLine(storedLine);
      if (storedView !== undefined) setActiveView(storedView);
      if (storedCar !== undefined) setSelectedCarrierId(storedCar);
      if (storedSolution !== undefined) setSelectedSolutionId(storedSolution);

      const restoredPhaseId =
        storedPhase !== undefined
          ? storedPhase
          : storedDetPh !== undefined
            ? storedDetPh
            : undefined;

      if (restoredPhaseId !== undefined) {
        setSelectedPhaseId(restoredPhaseId);
        setDetailPhaseId(restoredPhaseId);
      }

      if (storedBestMetric !== undefined) {
        setBestMetric(storedBestMetric || "unit");
      }

      if (snapTender) setTenderInfo(snapTender);
      if (Array.isArray(snapPhases)) setPhases(snapPhases);
      if (snapLastSyncAt !== undefined) setLastSyncAt(snapLastSyncAt);
      if (Array.isArray(snapTenderSchema)) {
        setTenderSchemaOptions(snapTenderSchema);
      }
      if (snapTenderFK && typeof snapTenderFK === "object") {
        setTenderFkData(snapTenderFK);
      }

      hydratedOnceRef.current = true;
      nsSnapshotRef.current = snap;
    } else {
      const seed = [
        { key: K.owner, value: String(selectedTab) },
        { key: K.operation, value: operation },
        { key: K.sessionLine, value: sessionLine },
        { key: K.activeView, value: activeView },
        { key: K.selectedCarrierId, value: selectedCarrierId },
        { key: K.selectedPhaseId, value: selectedPhaseId },
        { key: K.detailPhaseId, value: detailPhaseId },
        { key: K.selectedSolutionId, value: selectedSolutionId },
        { key: K.bestMetric, value: bestMetric },
        { key: K.tenderInfo, value: {} },
        { key: K.phases, value: [] },
        { key: K.lastSyncAt, value: null },
        { key: K.tenderSchemaOptions, value: [] },
        { key: K.tenderFkData, value: {} },
      ];

      dispatch(M_StateActions.addM_State({ key: ns, value: seed }));
      writeToStorage(ns, seed);
      nsSnapshotRef.current = seed;
      hydratedOnceRef.current = false;
    }

    setTimeout(() => {
      rehydratingRef.current = false;
    }, 0);

    prevTabRef.current = selectedTab || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        if ((tenderSchemaOptions || []).length) return;

        const opts = await tenderOptionsService.options?.();
        if (cancelled) return;

        if (!isFailurePayload(opts)) {
          setTenderSchemaOptions(Array.isArray(opts) ? opts : []);
        } else {
          setTenderSchemaOptions([]);
        }

        setTenderFkData({});
      } catch {
        setTenderSchemaOptions([]);
        setTenderFkData({});
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeView, tenderOptionsService, tenderSchemaOptions]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current);
      flushSnapshot();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);

      if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current);
      flushSnapshot();
    };
  }, [flushSnapshot]);

  useEffect(() => {
    if (!activeNsRef.current || rehydratingRef.current) return;

    if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current);
    persistDebounceRef.current = setTimeout(() => flushSnapshot(), 200);

    return () => {
      if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current);
    };
  }, [
    operation,
    sessionLine,
    activeView,
    selectedCarrierId,
    selectedPhaseId,
    detailPhaseId,
    selectedSolutionId,
    bestMetric,
    tenderInfo,
    phases,
    lastSyncAt,
    tenderSchemaOptions,
    tenderFkData,
    flushSnapshot,
  ]);

  useEffect(() => {
    if (!isFirst) return;

    setIsFirst(false);
    dispatch(M_StateActions.removeM_State({ key: "operation" }));
    dispatch(M_StateActions.removeM_State({ key: "TenderLine" }));

    const ns = stateKeyFrom(id);
    if (!ns) return;

    const seed = [
      { key: K.owner, value: String(id) },
      { key: K.operation, value: operation },
      { key: K.sessionLine, value: sessionLine },
      { key: K.activeView, value: activeView },
      { key: K.selectedCarrierId, value: selectedCarrierId },
      { key: K.selectedPhaseId, value: selectedPhaseId },
      { key: K.detailPhaseId, value: detailPhaseId },
      { key: K.selectedSolutionId, value: selectedSolutionId },
      { key: K.bestMetric, value: bestMetric },
      { key: K.tenderInfo, value: tenderInfo },
      { key: K.phases, value: phases },
      { key: K.lastSyncAt, value: lastSyncAt },
      { key: K.tenderSchemaOptions, value: tenderSchemaOptions },
      { key: K.tenderFkData, value: tenderFkData },
    ];

    dispatch(M_StateActions.addM_State({ key: ns, value: seed }));
    writeToStorage(ns, seed);
    nsSnapshotRef.current = seed;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFirst]);

  //endregion

  //region Heavy reload triggers

  useEffect(() => {
    if (!selectedTab) return;
    setRefreshKey((prev) => prev + 1);
    setDistinctCarriersReloadKey((prev) => prev + 1);
  }, [selectedTab]);

  //endregion

  //region Global phase source of truth

  /**
   * Single global phase setter for the entire page.
   *
   * Important:
   * - Carrier view and lane views both use this same state;
   * - changing phase resets carrier-specific and solution-specific state;
   * - this triggers backend reloads for target-scoped datasets.
   *
   * @param {number|null} newPhaseId - Selected phase id.
   * @returns {void}
   */
  const setGlobalPhaseId = useCallback((newPhaseId) => {
    const normalizedPhaseId = newPhaseId ?? null;

    setSelectedPhaseId(normalizedPhaseId);
    setDetailPhaseId(normalizedPhaseId);
    setSelectedCarrierId(null);
    setSelectedSolutionId(null);
    setDistinctCarriersItems([]);
    setExternalPublication(null);
    setExternalPublicationLoaded(false);
    setDistinctCarriersReloadKey((prev) => prev + 1);
    setRefreshKey((prev) => prev + 1);
    setExternalPublicationReloadKey((prev) => prev + 1);
  }, []);

  //endregion

  //region Hydrated fallbacks

  const hvTender = useMemo(
    () =>
      Object.keys(tenderInfo || {}).length
        ? tenderInfo
        : pickKV(currentNsSnapshot, K.tenderInfo) || {},
    [tenderInfo, currentNsSnapshot]
  );

  const hvPhases = useMemo(
    () =>
      phases && phases.length
        ? phases
        : pickKV(currentNsSnapshot, K.phases) || [],
    [phases, currentNsSnapshot]
  );

  const hvQuotesAll = useMemo(() => quotes || [], [quotes]);
  const hvQuotesAccepted = useMemo(() => quotesAccepted || [], [quotesAccepted]);
  const hvLanes = useMemo(() => lanes || [], [lanes]);
  const hvAllLanes = useMemo(() => allLanes || [], [allLanes]);
  const hvSummaryRows = useMemo(() => summaryRows || [], [summaryRows]);

  const hvLaneSummaries = useMemo(
    () => laneSummariesRows || [],
    [laneSummariesRows]
  );

  const hvLaneSummariesTenderWide = useMemo(
    () => laneSummariesTenderWideRows || [],
    [laneSummariesTenderWideRows]
  );
  const hvLaneSummariesAcceptedTenderWide = useMemo(
    () => laneSummariesAcceptedTenderWideRows || [],
    [laneSummariesAcceptedTenderWideRows]
  );

  const hvAnalysisSolutions = useMemo(
    () => analysisSolutions || [],
    [analysisSolutions]
  );

  //endregion

  //region Derived state

  const isSpotTender =
    Number(hvTender?.TenderTypeId) === 1 ||
    String(hvTender?.TenderType || "").toLowerCase() === "spot";

  const isTenderFinished = useMemo(() => {
    const stateValue = Number(hvTender?.State ?? hvTender?.state ?? 0);
    const finishedFlag = hvTender?.IsFinished ?? hvTender?.isFinished ?? false;
    return stateValue === 3 || finishedFlag === true;
  }, [hvTender]);

  /**
   * Resolves the current account id used for OpenSyllo integration detection.
   */
  const currentAccountId = useMemo(() => {
    const candidates = [
      hvTender?.AccountId,
      hvTender?.accountId,
      sessionLine?.AccountId,
      sessionLine?.accountId,
      globalLine?.AccountId,
      globalLine?.accountId,
      getCurrentAccountIdFromToken(),
    ];

    for (const candidate of candidates) {
      const value = toPositiveNumberOrNull(candidate);
      if (value != null) return value;
    }

    return null;
  }, [
    hvTender?.AccountId,
    hvTender?.accountId,
    sessionLine?.AccountId,
    sessionLine?.accountId,
    globalLine?.AccountId,
    globalLine?.accountId,
  ]);

  /**
   * Current phase used by current-target views.
   *
   * Spot mode has no phase scope.
   */
  const lanesPhaseId = useMemo(() => {
    if (isSpotTender) return null;
    return selectedPhaseId;
  }, [isSpotTender, selectedPhaseId]);

  /**
   * Real last phase id derived from the phase chain.
   */
  const lastPhaseId = useMemo(() => {
    if (isSpotTender) return null;
    return resolveLastPhaseId(hvPhases);
  }, [isSpotTender, hvPhases]);

  /**
 * Determines whether the current Tender / TenderPhase is allowed to expose
 * external-publication decision actions.
 *
 * Rules:
 * - Spot Tender: the Tender itself is the decision target.
 * - Non-Spot Tender: only the currently selected last phase can expose these actions.
 *
 * Previous phases must not expose external publication or carrier-interest actions,
 * because they are already historical / closed decision contexts.
 */
  const isCurrentExternalPublicationDecisionTarget = useMemo(() => {
    if (isSpotTender) return true;

    if (selectedPhaseId == null || lastPhaseId == null) return false;

    return Number(selectedPhaseId) === Number(lastPhaseId);
  }, [isSpotTender, selectedPhaseId, lastPhaseId]);

  /**
   * Keeps detailPhaseId aligned with the global selected phase.
   */
  useEffect(() => {
    if (isSpotTender) return;

    if (selectedPhaseId == null && detailPhaseId != null) {
      setSelectedPhaseId(detailPhaseId);
      return;
    }

    if (selectedPhaseId != null && detailPhaseId !== selectedPhaseId) {
      setDetailPhaseId(selectedPhaseId);
    }
  }, [isSpotTender, selectedPhaseId, detailPhaseId]);

  //endregion

  //region External publication state

  /**
   * Resolves the current external publication target.
   *
   * Spot Tenders publish the Tender itself.
   * Non-Spot Tenders publish the selected TenderPhase.
   */
  const currentExternalPublicationTarget = useMemo(() => {
    const tenderId = toPositiveNumberOrNull(sessionLine?.Id ?? hvTender?.Id);

    if (!tenderId) {
      return {
        targetType: null,
        targetEntityId: null,
        targetLabel: null,
      };
    }

    if (isSpotTender) {
      return {
        targetType: EXTERNAL_PUBLICATION_TARGET_TYPE.Tender,
        targetEntityId: tenderId,
        targetLabel: "Tender",
      };
    }

    const phaseId = toPositiveNumberOrNull(selectedPhaseId ?? lastPhaseId);

    return {
      targetType: phaseId
        ? EXTERNAL_PUBLICATION_TARGET_TYPE.TenderPhase
        : null,
      targetEntityId: phaseId,
      targetLabel: phaseId ? "TenderPhase" : null,
    };
  }, [sessionLine?.Id, hvTender?.Id, isSpotTender, selectedPhaseId, lastPhaseId]);

  const activeExternalPublication = useMemo(
    () =>
      isExternalPublicationActive(externalPublication)
        ? externalPublication
        : null,
    [externalPublication]
  );

  /**
   * Controls whether OpenSyllo external-publication actions can be exposed
   * in the current page context.
   *
   * Actions are available only when:
   * - the page is in quotation workflow;
   * - the Tender is not finished;
   * - the current Tender / TenderPhase is an eligible decision target;
   *   - Spot: the Tender itself;
   *   - Non-Spot: only the selected last phase;
   * - the OpenSyllo integration check has already completed;
   * - the account has an enabled OpenSyllo integration;
   * - the external-publication state has already been loaded;
   * - there is a valid target entity id to publish or inspect.
   */
  const shouldExposeOpenSylloActions =
    isQuotationWorkflow &&
    !isTenderFinished &&
    isCurrentExternalPublicationDecisionTarget &&
    openSylloIntegrationChecked &&
    hasOpenSylloIntegration &&
    externalPublicationLoaded &&
    currentExternalPublicationTarget.targetEntityId != null;

  /**
 * Shows the external publication button when OpenSyllo actions are allowed
 * and there is still no active external publication for the current decision target.
 *
 * This means:
 * - Spot: the Tender has not yet been published externally;
 * - Non-Spot: the selected last phase has not yet been published externally.
 */
  const shouldShowExternalPublishButton =
    shouldExposeOpenSylloActions && !activeExternalPublication;

  /**
   * Shows the carrier interests button when OpenSyllo actions are allowed
   * and an active external publication already exists for the current decision target.
   *
   * This means the Tender / selected last phase was already published externally,
   * so the user should review the external carrier interests instead of publishing again.
   */
  const shouldShowCarrierInterestsButton =
    shouldExposeOpenSylloActions && !!activeExternalPublication;

  /**
   * Loads whether the current account has an enabled OpenSyllo integration.
   */
  useEffect(() => {
    let cancelled = false;

    if (!isQuotationWorkflow) {
      setHasOpenSylloIntegration(false);
      setOpenSylloIntegrationChecked(false);
      setExternalPublication(null);
      setExternalPublicationLoaded(false);
      return;
    }

    if (!currentAccountId) {
      setHasOpenSylloIntegration(false);
      setOpenSylloIntegrationChecked(true);
      setExternalPublication(null);
      setExternalPublicationLoaded(true);
      return;
    }

    const run = async () => {
      try {
        const filter = encodeURIComponent(`AccountId eq ${Number(currentAccountId)}`);
        const res = await accountIntegrationsService.filter(
          `?Page=1&PageSize=20&Filter=${filter}`
        );

        if (cancelled) return;

        if (isFailurePayload(res)) {
          setHasOpenSylloIntegration(false);
          setOpenSylloIntegrationChecked(true);
          return;
        }

        const items = norm(res);
        setHasOpenSylloIntegration(items.some(isEnabledOpenSylloIntegration));
        setOpenSylloIntegrationChecked(true);
      } catch {
        if (!cancelled) {
          setHasOpenSylloIntegration(false);
          setOpenSylloIntegrationChecked(true);
        }
      }
    };

    setOpenSylloIntegrationChecked(false);
    run();

    return () => {
      cancelled = true;
    };
  }, [
    isQuotationWorkflow,
    currentAccountId,
    accountIntegrationsService,
  ]);

  /**
   * Loads the current external publication for the active Tender or TenderPhase target.
   */
  useEffect(() => {
    let cancelled = false;

    if (!isQuotationWorkflow || !hasOpenSylloIntegration) {
      setExternalPublication(null);
      setExternalPublicationLoaded(false);
      return;
    }

    if (currentExternalPublicationTarget.targetEntityId == null) {
      setExternalPublication(null);
      setExternalPublicationLoaded(true);
      return;
    }

    const run = async () => {
      const targetId = currentExternalPublicationTarget.targetEntityId;
      const service = isSpotTender ? tenderService : phasesService;

      try {
        const res = await service.getId(`${targetId}/external-publication`);

        if (cancelled) return;

        if (isExternalPublicationNotFoundPayload(res)) {
          setExternalPublication(null);
          setExternalPublicationLoaded(true);
          return;
        }

        if (isFailurePayload(res) || typeof res === "string") {
          setExternalPublication(null);
          setExternalPublicationLoaded(true);
          return;
        }

        setExternalPublication(res || null);
        setExternalPublicationLoaded(true);
      } catch (err) {
        if (cancelled) return;

        if (isExternalPublicationNotFoundPayload(err)) {
          setExternalPublication(null);
          setExternalPublicationLoaded(true);
          return;
        }

        setExternalPublication(null);
        setExternalPublicationLoaded(true);
      }
    };

    setExternalPublicationLoaded(false);
    run();

    return () => {
      cancelled = true;
    };
  }, [
    isQuotationWorkflow,
    hasOpenSylloIntegration,
    currentExternalPublicationTarget.targetEntityId,
    isSpotTender,
    tenderService,
    phasesService,
    externalPublicationReloadKey,
  ]);

  /**
   * Prevents the placeholder interests view from remaining selected when the
   * target no longer has an active external publication.
   */
  useEffect(() => {
    if (activeView !== "carrierInterests") return;

    const checksFinished =
      openSylloIntegrationChecked && externalPublicationLoaded;

    if (!checksFinished) return;

    if (!shouldShowCarrierInterestsButton) {
      setActiveView("inQuotation");
    }
  }, [
    activeView,
    openSylloIntegrationChecked,
    externalPublicationLoaded,
    shouldShowCarrierInterestsButton,
  ]);

  //endregion

  //region Reusable select options

  /**
   * Phase options reused by:
   * - carrier view
   * - acceptedByLane view
   * - totalByLane view
   *
   * All of them point to the same global selectedPhaseId state.
   */
  const phaseOptions = useMemo(
    () =>
      (hvPhases || []).map((phase) => ({
        label: phase.Name || `${tOr("Phase", "Phase")} ${phase.Id}`,
        value: phase.Id,
      })),
    [hvPhases, tOr]
  );

  const selectedPhaseOption = useMemo(
    () =>
      phaseOptions.find((option) => option.value === (selectedPhaseId ?? null)) ||
      null,
    [phaseOptions, selectedPhaseId]
  );

  //endregion

  //region Tab title

  useEffect(() => {
    if (!renameTab) return;

    const ownerId =
      pickKV(currentNsSnapshot, K.owner) || activeTidRef.current || id;
    if (!ownerId) return;

    const base = isAnalysisWorkflow
      ? tOr("TenderInAnalysis", "Tender In Analysis")
      : tOr("TenderOpen", "Open Tender");

    const viewKey =
      activeView === "carrier"
        ? tOr("CarrierDetails", "Carrier Details")
        : activeView === "acceptedByLane"
          ? tOr("AcceptedByLane", "Accepted By Lane")
          : activeView === "totalByLane"
            ? tOr("TotalByLane", "Total By Lane")
            : activeView === "summary"
              ? tOr("Summary", "Summary")
              : activeView === "carrierInterests"
                ? tOr("CarrierInterests", "Carrier Interests")
                : activeView === "inQuotation"
                  ? tOr("InQuotation", "In Quotation")
                  : tOr("Details", "Details");

    const snapshotName = pickKV(currentNsSnapshot, K.tenderInfo)?.Name;
    const name = (tenderInfo?.Name || snapshotName || "").trim();

    const label = name
      ? `${base} — ${name} (${viewKey})`
      : `${base} (${viewKey})`;

    renameTab(String(ownerId), label);
  }, [
    activeView,
    tenderInfo?.Name,
    currentNsSnapshot,
    id,
    renameTab,
    tOr,
    isAnalysisWorkflow,
  ]);

  //endregion

  //region Main data loader

  useEffect(() => {
    let cancelled = false;
    const lineId = sessionLine?.Id;
    if (!lineId) return;

    const hasMinimumHeavyData =
      Array.isArray(quotes) &&
      Array.isArray(allLanes) &&
      Array.isArray(summaryRows);

    const hasHydratedData =
      hydratedOnceRef.current &&
      Number(tenderInfo?.Id) === Number(lineId) &&
      hasMinimumHeavyData;

    const syncMaxAgeMs = 30_000;
    const lastSync = Number(lastSyncAtRef.current);
    const isFreshEnough =
      Number.isFinite(lastSync) && Date.now() - lastSync < syncMaxAgeMs;

    if (hasHydratedData && refreshKey === 0 && isFreshEnough) return;

    /**
     * Loads phases and carrier universe for the current tender.
     *
     * @param {object} args - Loader arguments.
     * @param {object} args.tender - Tender payload.
     * @param {number} args.lineId - Tender id.
     * @param {boolean} args.isSpot - Spot flag.
     * @returns {Promise<Array>} Loaded phases.
     */
    const loadPhasesPaged = async ({ tender, lineId: tenderId, isSpot }) => {
      let phasesList = Array.isArray(tender?.Phases) ? tender.Phases : [];

      if (!isSpot && phasesList.length === 0) {
        const pageSize = 20;
        let page = 1;
        const acc = [];
        const seen = new Set();
        const filter = encodeURIComponent(
          `TenderId eq ${tenderId} and Inactive eq false`
        );

        while (true) {
          if (cancelled) break;

          const res = await phasesService.filter(
            `?Page=${page}&PageSize=${pageSize}&filter=${filter}`
          );

          if (cancelled) break;
          if (isFailurePayload(res)) break;

          const items = norm(res);
          if (!items.length) break;

          for (const item of items) {
            const pid = item?.Id;
            if (pid != null && !seen.has(pid)) {
              seen.add(pid);
              acc.push(item);
            }
          }

          const totalPages = Number(
            res?.TotalPages ??
            res?.totalPages ??
            (items.length < pageSize ? page : page + 1)
          );

          if (page >= totalPages) break;
          page += 1;
        }

        phasesList = acc;
      }

      if (cancelled) return phasesList;
      setPhases(phasesList);

      if (!isSpot && phasesList.length > 0 && selectedPhaseId == null) {
        const lastIds = phasesList
          .map((p) => p.LastTenderPhaseId)
          .filter((pid) => pid != null);

        const mostRecent =
          phasesList.find((p) => !lastIds.includes(p.Id)) ||
          phasesList[phasesList.length - 1];

        const initialPhaseId = mostRecent?.Id ?? null;
        setSelectedPhaseId(initialPhaseId);
        setDetailPhaseId(initialPhaseId);
      }

      if (!isSpot) {
        const results = await Promise.all(
          (phasesList || []).map(async (phase) => {
            const phaseId = phase?.Id ?? phase?.id;
            if (phaseId == null) return [];

            let rows = [];

            try {
              const primary = await tenderPhaseCarriersService.getId(
                `tenderPhase/${phaseId}`
              );
              const normalized = isFailurePayload(primary) ? [] : norm(primary);
              rows = Array.isArray(normalized) ? normalized : [];
            } catch {
              rows = [];
            }

            if (!rows.length) {
              try {
                const fallback = await phaseCarriersService.getId(`${phaseId}/Carriers`);
                const normalized = isFailurePayload(fallback) ? [] : norm(fallback);
                rows = Array.isArray(normalized) ? normalized : [];
              } catch {
                rows = [];
              }
            }

            return rows
              .map((row) => normalizeInvitedCarrierRow(row, phaseId))
              .filter(Boolean);
          })
        );

        if (!cancelled) {
          setCarriers(mergeInvitedCarriers(results.flat()));
        }
      } else {
        let primaryRows = [];

        try {
          const primary = await tenderCarriersService.getId(`tender/${tenderId}`);
          const normalized = isFailurePayload(primary) ? [] : norm(primary);
          primaryRows = Array.isArray(normalized) ? normalized : [];
        } catch {
          primaryRows = [];
        }

        const spotCarriers = primaryRows.length
          ? primaryRows
          : Array.isArray(tender?.Carriers)
            ? tender.Carriers
            : [];

        const mapped = spotCarriers
          .map((carrierRow) => normalizeInvitedCarrierRow(carrierRow))
          .filter(Boolean);

        if (!cancelled) setCarriers(mapped);
      }

      return phasesList;
    };

    /**
     * Loads accepted and unaccepted quotes for the tender.
     *
     * The page keeps all quotes in memory and derives smaller view-specific
     * collections from this single source of truth.
     *
     * @param {object} args - Loader arguments.
     * @param {number} args.lineId - Tender id.
     * @returns {Promise<object>} Loaded quote collections.
     */
    const loadQuotesByAcceptancePaged = async ({ lineId: tenderId }) => {
      const pageSize = QUOTES_PAGE_SIZE;
      let page = 1;

      const acceptedAcc = [];
      const unacceptedAcc = [];
      const seen = new Set();

      while (true) {
        if (cancelled) break;

        const filterAccepted = encodeURIComponent(
          `Inactive eq false and TenderId eq ${tenderId} and IsAccepted eq true`
        );
        const filterUnaccepted = encodeURIComponent(
          `Inactive eq false and TenderId eq ${tenderId} and IsAccepted eq false`
        );

        const [resAccepted, resUnaccepted] = await Promise.all([
          tenderQuotesService.filter(
            `?Page=${page}&PageSize=${pageSize}&filter=${filterAccepted}`
          ),
          tenderQuotesService.filter(
            `?Page=${page}&PageSize=${pageSize}&filter=${filterUnaccepted}`
          ),
        ]);

        if (cancelled) break;

        const itemsAccepted = isFailurePayload(resAccepted)
          ? []
          : norm(resAccepted);
        const itemsUnaccepted = isFailurePayload(resUnaccepted)
          ? []
          : norm(resUnaccepted);

        for (const item of itemsAccepted) {
          const qid = getQuoteId(item);
          if (qid != null && !seen.has(qid)) {
            seen.add(qid);
            acceptedAcc.push(item);
          }
        }

        for (const item of itemsUnaccepted) {
          const qid = getQuoteId(item);
          if (qid != null && !seen.has(qid)) {
            seen.add(qid);
            unacceptedAcc.push(item);
          }
        }

        const totalPagesAccepted = Number(
          resAccepted?.TotalPages ??
          resAccepted?.totalPages ??
          (itemsAccepted.length < pageSize ? page : page + 1)
        );
        const totalPagesUnaccepted = Number(
          resUnaccepted?.TotalPages ??
          resUnaccepted?.totalPages ??
          (itemsUnaccepted.length < pageSize ? page : page + 1)
        );

        const totalPages = Math.max(totalPagesAccepted, totalPagesUnaccepted);
        if (page >= totalPages) break;
        page += 1;
      }

      const allQuotes = [...acceptedAcc, ...unacceptedAcc];

      if (!cancelled) {
        setQuotes(allQuotes);
        setQuotesAccepted(acceptedAcc);
        setQuotesUnaccepted(unacceptedAcc);
      }

      return { allQuotes, acceptedAcc, unacceptedAcc };
    };

    /**
     * Loads persisted TenderLane snapshot rows.
     *
     * These rows provide:
     * - lane universe metadata
     * - satisfied official snapshot metadata
     *
     * They are not used as the primary numeric source for Summary metrics.
     *
     * @param {object} args - Loader arguments.
     * @param {number} args.lineId - Tender id.
     * @returns {Promise<Array>} Loaded TenderLane rows.
     */
    const loadLaneSummaryPaged = async ({ lineId: tenderId }) => {
      const pageSize = SUMMARY_PAGE_SIZE;
      let page = 1;
      const acc = [];
      const seen = new Set();

      const filter = encodeURIComponent(`TenderId eq ${Number(tenderId)}`);

      while (true) {
        if (cancelled) break;

        const res = await tenderLanesSummaryService.filter(
          `?Page=${page}&PageSize=${pageSize}&Filter=${filter}`
        );

        if (cancelled) break;
        if (isFailurePayload(res)) break;

        const items = norm(res);
        if (!items.length) break;

        for (const item of items) {
          const laneId = item?.laneId ?? item?.LaneId ?? item?.LaneID ?? null;
          if (laneId == null) continue;

          const key = String(laneId);
          if (seen.has(key)) continue;
          seen.add(key);

          acc.push(item);
        }

        const totalPages = Number(
          res?.TotalPages ??
          res?.totalPages ??
          (items.length < pageSize ? page : page + 1)
        );

        if (page >= totalPages) break;
        page += 1;
      }

      if (!cancelled) setSummaryRows(acc);
      return acc;
    };

    const run = async () => {
      incLoad();

      try {
        const tender = await tenderService.getId(lineId);
        if (cancelled) return;

        if (isFailurePayload(tender)) {
          toastRef.current?.(
            "error",
            "",
            apiMessage(tender, tOr("ErrorLoadingData", "Error loading data"))
          );
          return;
        }

        setTenderInfo(tender);

        const tenderTypeId = Number(tender?.TenderTypeId ?? 0);
        const tenderTypeName = String(tender?.TenderType ?? "").toLowerCase();
        const isSpot = tenderTypeId === 1 || tenderTypeName === "spot";

        await Promise.allSettled([
          loadPhasesPaged({ tender, lineId, isSpot }),
          loadQuotesByAcceptancePaged({ lineId }),
          loadLaneSummaryPaged({ lineId }),
        ]);

        if (!cancelled) {
          hydratedOnceRef.current = true;
          setLastSyncAt(Date.now());
        }
      } catch (err) {
        if (!cancelled) {
          toastRef.current?.(
            "error",
            "",
            apiMessage(err, tOr("ErrorLoadingData", "Error loading data"))
          );
        }
      } finally {
        decLoad();
      }
    };

    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sessionLine?.Id,
    refreshKey,
    tenderInfo?.Id,
    selectedPhaseId,
    phasesService,
    tenderCarriersService,
    tenderPhaseCarriersService,
    phaseCarriersService,
    tenderQuotesService,
    tenderLanesSummaryService,
    incLoad,
    tenderService,
    tOr,
    decLoad,
  ]);

  //endregion

  //region Current analysis target

  /**
   * Resolves the current analysis target based on the active workflow context.
   *
   * Spot mode targets the Tender itself.
   * Non-Spot mode targets the globally selected TenderPhase.
   */
  const currentAnalysisTarget = useMemo(() => {
    const tenderId = Number(sessionLine?.Id ?? hvTender?.Id ?? 0);
    if (!Number.isFinite(tenderId) || tenderId <= 0) {
      return {
        targetType: null,
        targetEntityId: null,
        targetLabel: null,
      };
    }

    if (isSpotTender) {
      return {
        targetType: ANALYSIS_TARGET_TYPE.Tender,
        targetEntityId: tenderId,
        targetLabel: "Tender",
      };
    }

    const phaseId = selectedPhaseId ?? lastPhaseId ?? null;

    return {
      targetType: phaseId != null ? ANALYSIS_TARGET_TYPE.TenderPhase : null,
      targetEntityId: phaseId,
      targetLabel: phaseId != null ? "TenderPhase" : null,
    };
  }, [
    sessionLine?.Id,
    hvTender?.Id,
    isSpotTender,
    selectedPhaseId,
    lastPhaseId,
  ]);

  //endregion

  //region Analysis run loader

  useEffect(() => {
    let cancelled = false;

    if (!isAnalysisWorkflow) {
      setCurrentAnalysisRun(null);
      return;
    }

    if (
      currentAnalysisTarget.targetType == null ||
      currentAnalysisTarget.targetEntityId == null
    ) {
      setCurrentAnalysisRun(null);
      return;
    }

    const run = async () => {
      try {
        const filter = encodeURIComponent(
          `TargetType eq ${Number(
            currentAnalysisTarget.targetType
          )} and TargetEntityId eq ${Number(
            currentAnalysisTarget.targetEntityId
          )}`
        );

        const res = await tenderAnalysisRunsService.filter(
          `?Page=1&PageSize=1&Filter=${filter}`
        );

        if (cancelled) return;

        if (isFailurePayload(res)) {
          setCurrentAnalysisRun(null);
          return;
        }

        const items = norm(res);
        setCurrentAnalysisRun(items[0] || null);
      } catch {
        if (!cancelled) setCurrentAnalysisRun(null);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    isAnalysisWorkflow,
    currentAnalysisTarget.targetType,
    currentAnalysisTarget.targetEntityId,
    refreshKey,
    tenderAnalysisRunsService,
  ]);

  const analysisRunStatus = Number(currentAnalysisRun?.Status ?? 0);

  const isAnalysisSolutionReady = useMemo(() => {
    return (
      analysisRunStatus === ANALYSIS_RUN_STATUS.Imported ||
      currentAnalysisRun?.IsImported === true
    );
  }, [analysisRunStatus, currentAnalysisRun]);

  const analysisStatusInfo = useMemo(() => {
    if (!isAnalysisWorkflow) {
      return {
        tone: "neutral",
        title: "",
        message: "",
      };
    }

    if (
      currentAnalysisTarget.targetType == null ||
      currentAnalysisTarget.targetEntityId == null
    ) {
      return {
        tone: "warning",
        title: tOr("AnalysisStatusUnavailable", "Analysis status unavailable"),
        message: tOr(
          "SelectPhaseToLoadAnalysisStatus",
          "Select a phase to load the current analysis status."
        ),
      };
    }

    if (!currentAnalysisRun) {
      return {
        tone: "warning",
        title: tOr("AnalysisRunNotFound", "Analysis run not found"),
        message: tOr(
          "AnalysisRunNotFoundForCurrentTarget",
          "No analysis run was found for the current target."
        ),
      };
    }

    if (analysisRunStatus === ANALYSIS_RUN_STATUS.PendingExport) {
      return {
        tone: "info",
        title: tOr("AnalysisPendingExport", "Analysis pending export"),
        message: tOr(
          "AnalysisPendingExportInfo",
          "The analysis cycle was requested and is waiting for instance export."
        ),
      };
    }

    if (analysisRunStatus === ANALYSIS_RUN_STATUS.ExportRunning) {
      return {
        tone: "info",
        title: tOr("AnalysisExportRunning", "Analysis export running"),
        message: tOr(
          "AnalysisExportRunningInfo",
          "The analysis cycle is exporting the algorithm instance."
        ),
      };
    }

    if (analysisRunStatus === ANALYSIS_RUN_STATUS.ExportedAwaitingImport) {
      return {
        tone: "info",
        title: tOr("AnalysisAwaitingImport", "Analysis awaiting import"),
        message: tOr(
          "AnalysisAwaitingImportInfo",
          "The algorithm instance was exported and the solution import is still pending."
        ),
      };
    }

    if (analysisRunStatus === ANALYSIS_RUN_STATUS.ImportRunning) {
      return {
        tone: "info",
        title: tOr("AnalysisImportRunning", "Analysis import running"),
        message: tOr(
          "AnalysisImportRunningInfo",
          "The solution import is currently running."
        ),
      };
    }

    if (analysisRunStatus === ANALYSIS_RUN_STATUS.Imported) {
      return {
        tone: "success",
        title: tOr("AnalysisImported", "Analysis imported"),
        message:
          currentAnalysisRun?.HasSolutionArtifact === false
            ? tOr(
              "AnalysisImportedWithoutSolutionArtifact",
              "The analysis run was imported, but the solution artifact is not available."
            )
            : tOr(
              "AnalysisSuggestionsReady",
              "Suggested quotes are available for the current target."
            ),
      };
    }

    if (analysisRunStatus === ANALYSIS_RUN_STATUS.Failed) {
      const lastError =
        currentAnalysisRun?.LastError ?? currentAnalysisRun?.lastError ?? null;

      return {
        tone: "error",
        title: tOr("AnalysisFailed", "Analysis failed"),
        message: lastError
          ? `${tOr(
            "AnalysisFailedWithReason",
            "The analysis run failed."
          )} ${lastError}`
          : tOr("AnalysisFailedWithoutReason", "The analysis run failed."),
      };
    }

    if (analysisRunStatus === ANALYSIS_RUN_STATUS.Cancelled) {
      return {
        tone: "warning",
        title: tOr("AnalysisCancelled", "Analysis cancelled"),
        message: tOr(
          "AnalysisCancelledInfo",
          "The analysis run was cancelled and no further progress is expected."
        ),
      };
    }

    return {
      tone: "neutral",
      title: tOr("AnalysisStatusUnknown", "Analysis status unknown"),
      message: tOr(
        "AnalysisStatusUnknownInfo",
        "The current analysis status could not be resolved."
      ),
    };
  }, [
    isAnalysisWorkflow,
    currentAnalysisTarget.targetType,
    currentAnalysisTarget.targetEntityId,
    currentAnalysisRun,
    analysisRunStatus,
    tOr,
  ]);

  //endregion

  //region Analysis solutions loader

  /**
   * Loads imported solutions for the current analysis target.
   *
   * Spot mode filters by TenderId.
   * Non-Spot mode filters by the current global selectedPhaseId.
   *
   * Important architectural note:
   * - the page fetches only the current target solution collection from the BE;
   * - it does NOT keep a large multi-phase solution bag and then filter it on the FE.
   */
  useEffect(() => {
    let cancelled = false;

    if (!isAnalysisWorkflow) {
      setAnalysisSolutions([]);
      setSelectedSolutionId(null);
      return;
    }

    /*
     * Important:
     * - while the analysis run status is still being resolved, do NOT clear the
     *   persisted selectedSolutionId;
     * - otherwise the restored solution is lost before the imported solutions are loaded.
     */
    if (!isAnalysisSolutionReady) {
      setAnalysisSolutions([]);
      return;
    }

    const tenderId = Number(sessionLine?.Id ?? hvTender?.Id ?? 0);
    const phaseId = isSpotTender ? null : selectedPhaseId;

    /*
     * Important:
     * - phase changes already clear the selected solution in setGlobalPhaseId;
     * - here we should only avoid loading until the target is fully known.
     */
    if (!isSpotTender && phaseId == null) {
      setAnalysisSolutions([]);
      return;
    }

    if (!Number.isFinite(tenderId) || tenderId <= 0) {
      setAnalysisSolutions([]);
      return;
    }

    const shouldShowLoading =
      activeView === "carrier" ||
      activeView === "acceptedByLane" ||
      activeView === "totalByLane";

    const run = async () => {
      if (shouldShowLoading) incLoad();

      try {
        let page = 1;
        const acc = [];
        const seen = new Set();

        const filterParts = isSpotTender
          ? [`TenderId eq ${tenderId}`, "IsActive eq true"]
          : [`TenderPhaseId eq ${Number(phaseId)}`, "IsActive eq true"];

        const filter = encodeURIComponent(filterParts.join(" and "));

        while (true) {
          if (cancelled) return;

          const res = await tenderAnalysisSolutionsService.filter(
            `?Page=${page}&PageSize=${ANALYSIS_SOLUTIONS_PAGE_SIZE}&Filter=${filter}`
          );

          if (cancelled) return;

          if (isFailurePayload(res)) {
            setAnalysisSolutions([]);
            return;
          }

          const items = norm(res);
          if (!items.length) break;

          for (const item of items) {
            const solutionId = getSolutionId(item);
            if (solutionId == null) continue;

            const key = String(solutionId);
            if (seen.has(key)) continue;
            seen.add(key);
            acc.push(item);
          }

          const totalPages = Number(
            res?.TotalPages ??
            res?.totalPages ??
            (items.length < ANALYSIS_SOLUTIONS_PAGE_SIZE ? page : page + 1)
          );

          if (page >= totalPages) break;
          page += 1;
        }

        if (cancelled) return;

        setAnalysisSolutions(acc);

        /*
         * Preserve the restored solution when it still exists in the loaded target.
         * Only fallback to the first solution when the persisted one is no longer valid.
         */
        setSelectedSolutionId((prev) => {
          const prevExists = acc.some(
            (solution) => Number(getSolutionId(solution)) === Number(prev)
          );

          if (prevExists) return prev;

          const firstId = getSolutionId(acc[0]);
          return firstId ?? null;
        });
      } catch {
        if (!cancelled) {
          setAnalysisSolutions([]);
        }
      } finally {
        if (shouldShowLoading) decLoad();
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    isAnalysisWorkflow,
    isAnalysisSolutionReady,
    isSpotTender,
    sessionLine?.Id,
    hvTender?.Id,
    selectedPhaseId,
    refreshKey,
    activeView,
    tenderAnalysisSolutionsService,
    incLoad,
    decLoad,
  ]);

  //endregion

  //region Lane summaries loaders

  useEffect(() => {
    let cancelled = false;

    const tenderId = sessionLine?.Id ?? null;
    if (!tenderId) return;

    /**
     * Current-target phase id:
     * - Spot => null
     * - Non-Spot => current global selectedPhaseId
     */
    const currentTargetPhaseId = isSpotTender ? null : lanesPhaseId;

    /**
     * Loads one summary endpoint using the provided filter parts.
     *
     * @param {object} args - Loader arguments.
     * @param {BufferService} args.service - Service used to load the data.
     * @param {Array<string>} args.filterParts - Raw filter parts.
     * @param {number} args.pageSize - Requested page size.
     * @returns {Promise<Array>} Collected rows.
     */
    const fetchPaged = async ({ service, filterParts, pageSize }) => {
      let page = 1;
      const acc = [];
      const seen = new Set();

      const filter = encodeURIComponent(filterParts.join(" and "));

      while (true) {
        if (cancelled) return [];

        const res = await service.filter(
          `?Page=${page}&PageSize=${pageSize}&Filter=${filter}`
        );

        if (cancelled) return [];
        if (isFailurePayload(res)) return [];

        const items = Array.isArray(res?.Items)
          ? res.Items
          : Array.isArray(res)
            ? res
            : [];

        if (!items.length) break;

        for (const item of items) {
          const laneId =
            item?.LaneId ?? item?.laneId ?? item?.Id ?? item?.id ?? null;
          if (laneId == null) continue;

          const phaseKey = item?.TenderPhaseId ?? item?.tenderPhaseId ?? "null";
          const key = `${phaseKey}::${laneId}`;

          if (seen.has(key)) continue;
          seen.add(key);
          acc.push(item);
        }

        const totalPages = Number(
          res?.TotalPages ??
          res?.totalPages ??
          (items.length < pageSize ? page : page + 1)
        );

        if (page >= totalPages) break;
        page += 1;
      }

      return acc;
    };

    const run = async () => {
      const shouldShowLoading =
        activeView === "carrier" ||
        activeView === "acceptedByLane" ||
        activeView === "totalByLane" ||
        activeView === "summary" ||
        activeView === "inQuotation";

      if (shouldShowLoading) incLoad();

      try {
        const pageSize = 500;

        const commonFilterParts = [
          `TenderId eq ${Number(tenderId)}`,
          "Inactive eq false",
        ];

        /**
         * Current-target rows:
         * - Spot => Tender-wide by definition
         * - Non-Spot => narrowed by selectedPhaseId
         */
        const currentTargetAllFilterParts = [...commonFilterParts];
        const currentTargetAcceptedFilterParts = [...commonFilterParts];

        if (currentTargetPhaseId != null) {
          currentTargetAllFilterParts.push(
            `TenderPhaseId eq ${Number(currentTargetPhaseId)}`
          );
          currentTargetAcceptedFilterParts.push(
            `TenderPhaseId eq ${Number(currentTargetPhaseId)}`
          );
        }

        /**
         * Tender-wide rows:
         * - always scoped only by TenderId
         * - never narrowed by selectedPhaseId
         */
        const tenderWideAllFilterParts = [...commonFilterParts];
        const tenderWideAcceptedFilterParts = [...commonFilterParts];

        const [
          currentTargetAllRows,
          currentTargetAcceptedRows,
          tenderWideAllRows,
          tenderWideAcceptedRows,
        ] = await Promise.all([
          fetchPaged({
            service: laneSummariesService,
            filterParts: currentTargetAllFilterParts,
            pageSize,
          }),
          fetchPaged({
            service: laneSummariesAcceptedService,
            filterParts: currentTargetAcceptedFilterParts,
            pageSize,
          }),
          fetchPaged({
            service: laneSummariesService,
            filterParts: tenderWideAllFilterParts,
            pageSize,
          }),
          fetchPaged({
            service: laneSummariesAcceptedService,
            filterParts: tenderWideAcceptedFilterParts,
            pageSize,
          }),
        ]);

        if (cancelled) return;

        setLaneSummariesRows(currentTargetAllRows);
        setLaneSummariesAcceptedRows(currentTargetAcceptedRows);
        setLaneSummariesTenderWideRows(tenderWideAllRows);
        setLaneSummariesAcceptedTenderWideRows(tenderWideAcceptedRows);
      } catch {
        if (!cancelled) {
          setLaneSummariesRows([]);
          setLaneSummariesAcceptedRows([]);
          setLaneSummariesTenderWideRows([]);
          setLaneSummariesAcceptedTenderWideRows([]);
        }
      } finally {
        if (shouldShowLoading) decLoad();
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    sessionLine?.Id,
    isSpotTender,
    lanesPhaseId,
    refreshKey,
    activeView,
    laneSummariesService,
    laneSummariesAcceptedService,
    incLoad,
    decLoad,
  ]);

  //endregion

  //region Quote enrichment

  const hvQuotesAllEnriched = useMemo(
    () => withIsLastPhase(hvQuotesAll, lastPhaseId),
    [hvQuotesAll, lastPhaseId]
  );

  const hvQuotesAcceptedEnriched = useMemo(
    () => withIsLastPhase(hvQuotesAccepted, lastPhaseId),
    [hvQuotesAccepted, lastPhaseId]
  );

  //endregion

  //region Solution selection helpers

  /**
   * Normalizes the loaded current-target solutions into select options.
   *
   * Important:
   * - this is mapping only, not heavy FE filtering;
   * - the heavy scoping by Tender / TenderPhase already happened in the BE call.
   */
  const solutionOptions = useMemo(() => {
    return (Array.isArray(hvAnalysisSolutions) ? hvAnalysisSolutions : [])
      .map((solution) => {
        const solutionId = getSolutionId(solution);
        const sequence = getSolutionSequence(solution);
        const displayName =
          solution?.DisplayName ??
          solution?.displayName ??
          (sequence != null ? `Solution ${sequence}` : `Solution ${solutionId}`);

        return {
          label: displayName,
          value: solutionId,
        };
      })
      .filter((option) => option.value != null);
  }, [hvAnalysisSolutions]);

  const selectedSolutionOption = useMemo(
    () =>
      solutionOptions.find(
        (option) => option.value === (selectedSolutionId ?? null)
      ) || null,
    [solutionOptions, selectedSolutionId]
  );

  /**
   * Resolves the currently selected current-target solution payload.
   */
  const selectedSolution = useMemo(() => {
    if (selectedSolutionId == null) return null;

    return (
      (Array.isArray(hvAnalysisSolutions) ? hvAnalysisSolutions : []).find(
        (solution) =>
          Number(getSolutionId(solution)) === Number(selectedSolutionId)
      ) || null
    );
  }, [hvAnalysisSolutions, selectedSolutionId]);

  /**
   * Current selected solution awards.
   *
   * Important:
   * - the loader already fetched solutions for the current target only;
   * - therefore these awards are already target-scoped;
   * - we do NOT re-filter a large multi-target solution bag on the FE.
   */
  const selectedSolutionAwardsForCurrentTarget = useMemo(
    () => getSolutionAwards(selectedSolution),
    [selectedSolution]
  );

  /**
   * Narrows the selected solution awards to the selected carrier.
   *
   * This is a small, already current-target-scoped collection, so this local
   * filtering remains cheap and safe.
   */
  const selectedSolutionAwardsForCarrier = useMemo(() => {
    if (selectedCarrierId == null) return [];

    return selectedSolutionAwardsForCurrentTarget.filter(
      (award) => Number(getAwardCarrierId(award)) === Number(selectedCarrierId)
    );
  }, [selectedSolutionAwardsForCurrentTarget, selectedCarrierId]);

  /**
   * Suggested quote ids for the currently selected current-target solution.
   */
  const selectedSolutionSuggestedQuoteIds = useMemo(() => {
    return selectedSolutionAwardsForCurrentTarget
      .map((award) => getAwardQuoteId(award))
      .filter((quoteId) => quoteId != null);
  }, [selectedSolutionAwardsForCurrentTarget]);

  /**
   * Lane-indexed map of awards for the selected solution.
   */
  const selectedSolutionAwardsByLaneId = useMemo(() => {
    const map = new Map();

    for (const award of selectedSolutionAwardsForCurrentTarget) {
      const laneId = getAwardLaneId(award);
      if (laneId == null) continue;

      if (!map.has(laneId)) map.set(laneId, []);
      map.get(laneId).push(award);
    }

    return map;
  }, [selectedSolutionAwardsForCurrentTarget]);

  /**
 * Builds the translated summary sentence for the selected imported solution.
 *
 * The text follows the requirement:
 * "A sugestão da {Nome da solução} tem um custo total de {Bid Value total} {Currency}
 *  e atribui um total de {cargas atribuidas} de {cargas possiveis} possíveis"
 *
 * Important:
 * - totalCost is formatted with the current UI locale;
 * - tenderCurrency is read from the parent Tender;
 * - totalCostWithCurrency is passed as totalCost so existing i18n templates that
 *   already use {{totalCost}} automatically start showing the currency.
 *
 * We keep a translated fallback inline so the page still works even before the
 * i18n dictionaries are updated.
 */
  const selectedSolutionMetricsBannerMessage = useMemo(() => {
    if (!isAnalysisWorkflow || !isAnalysisSolutionReady || !selectedSolution) {
      return null;
    }

    const solutionName =
      getSolutionDisplayName(selectedSolution) ?? tOr("Solution", "Solution");

    const totalCostRaw = Number(getSolutionTotalCost(selectedSolution));
    const assignedLoadsRaw = Number(getSolutionAssignedLoads(selectedSolution));
    const possibleLoadsRaw = Number(getSolutionPossibleLoads(selectedSolution));

    if (
      !Number.isFinite(totalCostRaw) ||
      !Number.isFinite(assignedLoadsRaw) ||
      !Number.isFinite(possibleLoadsRaw)
    ) {
      return null;
    }

    const locale = i18n?.resolvedLanguage || i18n?.language || undefined;

    const numberFormatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    const totalCost = numberFormatter.format(totalCostRaw);
    const assignedLoads = numberFormatter.format(assignedLoadsRaw);
    const possibleLoads = numberFormatter.format(possibleLoadsRaw);

    /**
     * Tender currency belongs to the parent Tender.
     *
     * Example:
     * - totalCost = "1,200"
     * - tenderCurrency = "$"
     * - totalCostWithCurrency = "1,200 $"
     */
    const tenderCurrency = getTenderCurrencyLabel(hvTender);
    const totalCostWithCurrency = tenderCurrency
      ? `${totalCost} ${tenderCurrency}`
      : totalCost;

    const lang = String(locale || "en").toLowerCase();

    let fallback = `The suggestion for ${solutionName} has a total cost of ${totalCostWithCurrency} and assigns a total of ${assignedLoads} out of ${possibleLoads} possible loads.`;

    if (lang.startsWith("pt")) {
      fallback = `A sugestão da ${solutionName} tem um custo total de ${totalCostWithCurrency} e atribui um total de ${assignedLoads} de ${possibleLoads} cargas possíveis.`;
    } else if (lang.startsWith("es")) {
      fallback = `La sugerencia de ${solutionName} tiene un coste total de ${totalCostWithCurrency} y asigna un total de ${assignedLoads} de ${possibleLoads} cargas posibles.`;
    }

    return t("TenderAnalysisSolutionMetricsBanner", {
      solutionName,

      /**
       * Keep the existing placeholder name, but now already including currency.
       *
       * This means existing translations like:
       * "{{solutionName}} ... {{totalCost}} ..."
       *
       * will now render:
       * "Solution 1 ... 1,200 $ ..."
       */
      totalCost: totalCostWithCurrency,

      /**
       * Extra placeholders for future i18n templates, if needed.
       */
      totalCostValue: totalCost,
      tenderCurrency,

      assignedLoads,
      possibleLoads,
      defaultValue: fallback,
    });
  }, [
    isAnalysisWorkflow,
    isAnalysisSolutionReady,
    selectedSolution,
    hvTender,
    i18n?.resolvedLanguage,
    i18n?.language,
    t,
    tOr,
  ]);

  /**
   * The solution summary tile should appear only in the views where the user can
   * actually choose a solution from the existing SelectBox.
   *
   * This keeps the UX aligned with the current page behavior:
   * - carrier
   * - acceptedByLane
   * - totalByLane
   *
   * We intentionally do not show it in:
   * - inQuotation
   * - summary
   */
  const shouldRenderSelectedSolutionMetricsBanner = useMemo(() => {
    if (!isAnalysisWorkflow) return false;
    if (!isAnalysisSolutionReady) return false;
    if (!selectedSolutionMetricsBannerMessage) return false;
    if (!selectedSolution) return false;
    if (!solutionOptions.length) return false;

    return (
      activeView === "carrier" ||
      activeView === "acceptedByLane" ||
      activeView === "totalByLane"
    );
  }, [
    isAnalysisWorkflow,
    isAnalysisSolutionReady,
    selectedSolutionMetricsBannerMessage,
    selectedSolution,
    solutionOptions.length,
    activeView,
  ]);

  //endregion

  //region Best lane maps

  /**
   * Current-target all quotes:
   * - Spot => Tender-wide quotes
   * - Non-Spot => quotes for the current selected phase only
   *
   * These are used as the defensive fallback source for the carrier detail best map.
   *
   * Important:
   * - carrier detail compares the selected carrier against the current target quote set,
   *   not only against accepted quotes.
   */
  const currentTargetQuotes = useMemo(() => {
    const list = Array.isArray(hvQuotesAllEnriched)
      ? hvQuotesAllEnriched
      : [];

    if (isSpotTender) return list;
    if (selectedPhaseId == null) return [];

    return list.filter(
      (quote) => Number(getQuotePhaseId(quote)) === Number(selectedPhaseId)
    );
  }, [hvQuotesAllEnriched, isSpotTender, selectedPhaseId]);

  /**
   * Current-target best map:
   * - used by carrier detail
   * - phase-scoped in Non-Spot mode
   *
   * Important:
   * - summary rows remain the preferred source for:
   *      * unit
   *      * loads
   *      * etd
   * - BidValue best must fall back to quotes because the current summary endpoint
   *   is primarily unit-based and Bid min/max is not a reliable aggregate there.
   * - for carrier detail, the baseline must be built from the current target quote set,
   *   not only from accepted quotes.
   */
  const currentTargetLaneBestMap = useMemo(() => {
    const fromSummary = buildLaneBestMapFromSummaryRows(hvLaneSummaries);
    const fromQuotes = buildQuoteMetricsByLane(currentTargetQuotes);

    const merged = new Map(fromSummary);

    for (const [laneId, metrics] of fromQuotes.entries()) {
      const current = merged.get(laneId) || {};

      merged.set(laneId, {
        ...current,

        bestUnitValue:
          current.bestUnitValue ??
          current.bestUnitPrice ??
          current.minUnitPrice ??
          metrics.minUnitPrice ??
          null,

        bestUnitPrice:
          current.bestUnitPrice ??
          current.bestUnitValue ??
          current.minUnitPrice ??
          metrics.minUnitPrice ??
          null,

        minUnitPrice:
          current.minUnitPrice ??
          current.bestUnitPrice ??
          current.bestUnitValue ??
          metrics.minUnitPrice ??
          null,

        /**
         * Important:
         * - BidValue best must come from quotes first;
         * - the current summary endpoint is not a reliable source for bid min/max.
         */
        bestBidValue:
          metrics.minBidValue ??
          current.bestBidValue ??
          current.minBidValue ??
          null,

        minBidValue:
          metrics.minBidValue ??
          current.minBidValue ??
          current.bestBidValue ??
          null,

        bestLoadsValue:
          current.bestLoadsValue ??
          current.maxLoads ??
          metrics.maxLoads ??
          null,

        maxLoads:
          current.maxLoads ??
          current.bestLoadsValue ??
          metrics.maxLoads ??
          null,

        bestEtdValue:
          current.bestEtdValue ??
          current.minEstimatedTransitDays ??
          metrics.minEstimatedTransitDays ??
          null,

        minEstimatedTransitDays:
          current.minEstimatedTransitDays ??
          current.bestEtdValue ??
          metrics.minEstimatedTransitDays ??
          null,
      });
    }

    return merged;
  }, [hvLaneSummaries, currentTargetQuotes]);

  /**
   * Tender-wide accepted best map:
   * - used by AcceptedByLane
   */
  const acceptedByLaneBestMap = useMemo(
    () => buildLaneBestMapFromSummaryRows(hvLaneSummariesAcceptedTenderWide),
    [hvLaneSummariesAcceptedTenderWide]
  );

  /**
   * Tender-wide total best map:
   * - used by TotalByLane
   */
  const totalByLaneBestMap = useMemo(
    () => buildLaneBestMapFromSummaryRows(hvLaneSummariesTenderWide),
    [hvLaneSummariesTenderWide]
  );

  /**
   * Resolves the tender loads target used by:
   * - remaining loads in ViewLaneTable
   * - any FE-side "satisfaction" calculation that depends on total loads
   *
   * Important:
   * - in TenderOpenPage, hvTender may not always carry Loads reliably;
   * - sessionLine often still contains the correct target loads value;
   * - therefore we use a defensive fallback chain.
   */
  const tenderLoads = useMemo(() => {
    const candidates = [
      sessionLine?.Loads,
      sessionLine?.loads,
      hvTender?.Loads,
      hvTender?.loads,
      globalLine?.Loads,
      globalLine?.loads,
    ];

    for (const candidate of candidates) {
      const value = Number(candidate);
      if (Number.isFinite(value) && value > 0) {
        return value;
      }
    }

    return 0;
  }, [
    sessionLine?.Loads,
    sessionLine?.loads,
    hvTender?.Loads,
    hvTender?.loads,
    globalLine?.Loads,
    globalLine?.loads,
  ]);

  /**
   * Remaining loads for AcceptedByLane.
   *
   * Important:
   * - remaining lanes must consider only accepted loads;
   * - therefore this map uses the accepted tender-wide summaries.
   */
  const acceptedByLaneRemainingLoadsMap = useMemo(
    () =>
      buildRemainingLoadsByLaneMap({
        lanes: hvAllLanes,
        summaryRows: hvLaneSummariesAcceptedTenderWide,
        tenderLoads,
      }),
    [hvAllLanes, hvLaneSummariesAcceptedTenderWide, tenderLoads]
  );

  /**
   * Remaining loads for TotalByLane.
   *
   * Important:
   * - even in total mode, remaining lanes must consider only accepted loads;
   * - therefore this map also uses the accepted tender-wide summaries.
   */
  const totalByLaneRemainingLoadsMap = useMemo(
    () =>
      buildRemainingLoadsByLaneMap({
        lanes: hvAllLanes,
        summaryRows: hvLaneSummariesAcceptedTenderWide,
        tenderLoads,
      }),
    [hvAllLanes, hvLaneSummariesAcceptedTenderWide, tenderLoads]
  );

  //endregion

  //region Distinct carriers with quotes

  useEffect(() => {
    let cancelled = false;

    const tenderId = sessionLine?.Id;
    if (!tenderId) {
      setDistinctCarriersItems([]);
      return;
    }

    /**
     * Distinct carriers remain current-target scoped:
     * - Spot => Tender-wide
     * - Non-Spot => selected global phase
     */
    const phaseId = isSpotTender ? null : selectedPhaseId;

    const fetchDistinctCarriers = async () => {
      const shouldShowLoading = activeView === "carrier";
      if (shouldShowLoading) incLoad();

      try {
        const filterParts = [`TenderId eq ${tenderId}`, `Inactive eq false`];
        if (phaseId != null) filterParts.push(`TenderPhaseId eq ${phaseId}`);

        const filter = encodeURIComponent(filterParts.join(" and "));
        const pageSize = DISTINCT_CARRIERS_PAGE_SIZE;
        let page = 1;

        const acc = [];
        const seen = new Set();

        while (true) {
          if (cancelled) return;

          const res = await distinctCarriersService.filter(
            `?Page=${page}&PageSize=${pageSize}&filter=${filter}`
          );

          if (cancelled) return;
          if (isFailurePayload(res)) break;

          const items =
            res?.Items ??
            res?.items ??
            res?.Data?.Items ??
            res?.data?.items ??
            [];

          if (!Array.isArray(items) || items.length === 0) break;

          for (const row of items) {
            const carrier = row?.Carrier ?? row?.carrier ?? row ?? {};
            const carrierId = carrier?.CarrierId ?? carrier?.Id ?? null;
            if (carrierId == null) continue;

            const key = String(carrierId);
            if (seen.has(key)) continue;
            seen.add(key);

            acc.push(row);
          }

          const totalPages = Number(
            res?.TotalPages ??
            res?.totalPages ??
            (items.length < pageSize ? page : page + 1)
          );

          if (page >= totalPages) break;
          page += 1;
        }

        setDistinctCarriersItems(acc);
      } finally {
        if (shouldShowLoading) decLoad();
      }
    };

    fetchDistinctCarriers().catch(() => {
      if (!cancelled) setDistinctCarriersItems([]);
    });

    return () => {
      cancelled = true;
    };
  }, [
    sessionLine?.Id,
    isSpotTender,
    activeView,
    selectedPhaseId,
    refreshKey,
    distinctCarriersReloadKey,
    distinctCarriersService,
    incLoad,
    decLoad,
  ]);

  //endregion

  //region Lane loaders

  /**
   * Loads current-target lanes in flat form.
   *
   * Important:
   * - Spot => Tender-wide
   * - Non-Spot => phase-scoped by selected global phase
   *
   * These rows feed:
   * - InQuotationForm side components
   * - carrier detail view
   */
  const loadLanesPaged = useCallback(
    async ({ lineId, isSpot, phaseId }) => {
      const pageSize = LANES_PAGE_SIZE_FLAT;
      let page = 1;
      const acc = [];
      const seen = new Set();

      const baseFilter = isSpot
        ? `TenderId eq ${Number(lineId)}`
        : `TenderId eq ${Number(lineId)} and SatisfiedInTenderPhaseId eq ${Number(
          phaseId
        )}`;

      const qFilter = encodeURIComponent(baseFilter);

      while (true) {
        const lanesRes = await tenderLanesFlatService.filter(
          `?Page=${page}&PageSize=${pageSize}&Filter=${qFilter}`
        );

        if (isFailurePayload(lanesRes)) break;

        const items = norm(lanesRes);
        if (!items.length) break;

        for (const item of items) {
          const laneId = item?.laneId ?? item?.LaneId ?? null;
          if (laneId == null || seen.has(laneId)) continue;

          seen.add(laneId);

          const origin = String(
            item?.sourceLocation ?? item?.SourceLocation ?? ""
          ).trim();
          const destination = String(
            item?.destinationLocation ?? item?.DestinationLocation ?? ""
          ).trim();

          acc.push({
            ...item,
            LaneId: Number(laneId),
            Origin: origin,
            Destination: destination,
            LaneSource: origin,
            LaneDestination: destination,
            IsActive:
              item?.inactive != null
                ? !item.inactive
                : item?.Inactive != null
                  ? !item.Inactive
                  : true,
          });
        }

        const totalPages = Number(
          lanesRes?.TotalPages ??
          lanesRes?.totalPages ??
          (items.length < pageSize ? page : page + 1)
        );

        if (page >= totalPages) break;
        page += 1;
      }

      setLanes(acc);
      return acc;
    },
    [tenderLanesFlatService]
  );

  /**
   * Loads the Tender-wide lane universe.
   *
   * These rows feed:
   * - acceptedByLane
   * - totalByLane
   * - summary
   */
  const loadAllLanesPaged = useCallback(
    async ({ lineId }) => {
      const pageSize = ALL_LANES_PAGE_SIZE;
      let page = 1;
      const acc = [];
      const seen = new Set();

      const filter = encodeURIComponent(`TenderId eq ${Number(lineId)}`);

      while (true) {
        const res = await tenderLanesSummaryService.filter(
          `?Page=${page}&PageSize=${pageSize}&Filter=${filter}`
        );

        if (isFailurePayload(res)) break;

        const items = norm(res);
        if (!items.length) break;

        for (const item of items) {
          const laneId = item?.laneId ?? item?.LaneId ?? item?.LaneID ?? null;
          if (laneId == null) continue;

          const key = String(laneId);
          if (seen.has(key)) continue;
          seen.add(key);

          const originRaw =
            item?.sourceLocation ??
            item?.SourceLocation ??
            item?.origin ??
            item?.Origin ??
            item?.laneSource ??
            item?.LaneSource ??
            item?.LaneOrigin ??
            item?.laneOrigin ??
            "";

          const destinationRaw =
            item?.destinationLocation ??
            item?.DestinationLocation ??
            item?.destination ??
            item?.Destination ??
            item?.laneDestination ??
            item?.LaneDestination ??
            item?.LaneDestinationName ??
            item?.laneDestinationName ??
            "";

          const origin = String(originRaw || "").trim();
          const destination = String(destinationRaw || "").trim();

          acc.push({
            ...item,
            LaneId: Number(laneId),
            Origin: origin,
            Destination: destination,
            LaneSource: origin,
            LaneDestination: destination,
            IsActive:
              item?.inactive != null
                ? !item.inactive
                : item?.Inactive != null
                  ? !item.Inactive
                  : true,
          });
        }

        const totalPages = Number(
          res?.TotalPages ??
          res?.totalPages ??
          (items.length < pageSize ? page : page + 1)
        );

        if (page >= totalPages) break;
        page += 1;
      }

      setAllLanes(acc);
      return acc;
    },
    [tenderLanesSummaryService]
  );

  useEffect(() => {
    let cancelled = false;
    const lineId = sessionLine?.Id;
    if (!lineId) return;

    (async () => {
      try {
        if (isSpotTender) {
          const res = await loadLanesPaged({
            lineId,
            isSpot: true,
            phaseId: null,
          });

          if (!cancelled && Array.isArray(res)) setLanes(res);
          return;
        }

        if (lanesPhaseId == null) return;

        const res = await loadLanesPaged({
          lineId,
          isSpot: false,
          phaseId: lanesPhaseId,
        });

        if (!cancelled && Array.isArray(res)) setLanes(res);
      } catch {
        if (!cancelled) setLanes([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionLine?.Id, isSpotTender, lanesPhaseId, loadLanesPaged]);

  useEffect(() => {
    let cancelled = false;
    const lineId = sessionLine?.Id;
    if (!lineId) return;

    (async () => {
      try {
        const res = await loadAllLanesPaged({ lineId });
        if (!cancelled && Array.isArray(res)) setAllLanes(res);
      } catch {
        if (!cancelled) setAllLanes([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionLine?.Id, loadAllLanesPaged]);

  //endregion

  //region Summary rows

  /**
   * Summary view is Tender-wide.
   *
   * Therefore it must use:
   * - Tender-wide lane universe
   * - Tender-wide accepted summary rows
   *
   * This keeps it aligned with the Tender-wide lane semantics.
   */
  const summaryLaneRows = useMemo(() => {
    const lanesList = Array.isArray(hvAllLanes) ? hvAllLanes : [];
    const snapshotRows = Array.isArray(hvSummaryRows) ? hvSummaryRows : [];
    const acceptedRows = Array.isArray(hvLaneSummariesAcceptedTenderWide)
      ? hvLaneSummariesAcceptedTenderWide
      : [];

    const snapshotByLane = new Map();
    const acceptedByLane = new Map();
    const phaseById = new Map((hvPhases || []).map((phase) => [phase.Id, phase]));
    const tenderLoads = Number(hvTender?.Loads ?? 0);

    for (const row of snapshotRows) {
      const laneIdRaw = row?.LaneId ?? row?.laneId ?? row?.Id ?? row?.id ?? null;
      const laneId = laneIdRaw != null ? Number(laneIdRaw) : null;
      if (!Number.isFinite(laneId)) continue;
      snapshotByLane.set(laneId, row);
    }

    for (const row of acceptedRows) {
      const laneIdRaw = row?.LaneId ?? row?.laneId ?? row?.Id ?? row?.id ?? null;
      const laneId = laneIdRaw != null ? Number(laneIdRaw) : null;
      if (!Number.isFinite(laneId)) continue;
      acceptedByLane.set(laneId, row);
    }

    const asNum = (value) => {
      const n = Number(value);
      return Number.isFinite(n) ? n : null;
    };

    return lanesList
      .map((lane) => {
        const laneId = Number(getLaneId(lane));
        if (!Number.isFinite(laneId)) return null;

        const snapshot = snapshotByLane.get(laneId) || null;
        const accepted = acceptedByLane.get(laneId) || null;

        const origin =
          (getLaneOrigin(lane) || "").trim() ||
          String(
            snapshot?.SourceLocation ??
            snapshot?.sourceLocation ??
            snapshot?.LaneSource ??
            snapshot?.laneSource ??
            snapshot?.Origin ??
            snapshot?.origin ??
            ""
          ).trim() ||
          "-";

        const destination =
          (getLaneDestination(lane) || "").trim() ||
          String(
            snapshot?.DestinationLocation ??
            snapshot?.destinationLocation ??
            snapshot?.LaneDestination ??
            snapshot?.laneDestination ??
            snapshot?.Destination ??
            snapshot?.destination ??
            ""
          ).trim() ||
          "-";

        const totalQuotes =
          asNum(accepted?.TotalQuotes ?? accepted?.totalQuotes) ?? 0;

        const totalBidValue = asNum(
          accepted?.TotalBidValue ?? accepted?.totalBidValue
        );

        const maxUnitPrice = asNum(
          accepted?.MaxUnitPrice ??
          accepted?.maxUnitPrice ??
          accepted?.MaxBidValue ??
          accepted?.maxBidValue
        );

        const minUnitPrice = asNum(
          accepted?.MinUnitPrice ??
          accepted?.minUnitPrice ??
          accepted?.MinBidValue ??
          accepted?.minBidValue
        );

        const totalLoads =
          asNum(accepted?.TotalLoads ?? accepted?.totalLoads) ?? 0;
        const maxLoads = asNum(accepted?.MaxLoads ?? accepted?.maxLoads);
        const minLoads = asNum(accepted?.MinLoads ?? accepted?.minLoads);
        const maxEstimatedTransitDays = asNum(
          accepted?.MaxEstimatedTransitDays ?? accepted?.maxEstimatedTransitDays
        );
        const minEstimatedTransitDays = asNum(
          accepted?.MinEstimatedTransitDays ?? accepted?.minEstimatedTransitDays
        );

        const snapshotIsSatisfied = Boolean(
          snapshot?.IsSatisfied ?? snapshot?.isSatisfied ?? false
        );
        const snapshotSatisfiedAt = getSummarySatisfiedAt(snapshot);
        const snapshotSatisfiedPhaseId = getSummarySatisfiedPhaseId(snapshot);

        const snapshotPhase =
          snapshotSatisfiedPhaseId != null
            ? phaseById.get(snapshotSatisfiedPhaseId) || null
            : null;

        const snapshotSatisfiedPhaseName =
          snapshot?.SatisfiedInTenderPhase ??
          snapshot?.satisfiedInTenderPhase ??
          snapshotPhase?.Name ??
          snapshotPhase?.name ??
          null;

        const virtualSatisfied =
          !snapshotIsSatisfied &&
          !snapshotSatisfiedAt &&
          tenderLoads > 0 &&
          totalLoads >= tenderLoads;

        const isSatisfied = snapshotIsSatisfied || virtualSatisfied;

        const satisfiedAt = snapshotSatisfiedAt
          ? snapshotSatisfiedAt
          : virtualSatisfied
            ? "Current"
            : null;

        const satisfiedInPhase = snapshotSatisfiedPhaseName
          ? snapshotSatisfiedPhaseName
          : virtualSatisfied && !isSpotTender
            ? tOr("CurrentPhase", "Current")
            : null;

        return {
          laneId,
          origin,
          destination,
          totalQuotes,
          totalBidValue,
          maxUnitPrice,
          minUnitPrice,

          /**
           * Compatibility aliases kept for consumers that still read these keys.
           */
          maxBidValue: maxUnitPrice,
          minBidValue: minUnitPrice,

          totalLoads,
          maxLoads,
          minLoads,
          maxEstimatedTransitDays,
          minEstimatedTransitDays,
          isSatisfied,
          satisfiedAt,
          satisfiedInPhase,
        };
      })
      .filter(Boolean);
  }, [
    hvAllLanes,
    hvSummaryRows,
    hvLaneSummariesAcceptedTenderWide,
    hvPhases,
    hvTender?.Loads,
    isSpotTender,
    tOr,
  ]);

  //endregion

  //region UI helpers

  const refreshCarrierDetailTable = useCallback((carrierId) => {
    setSelectedCarrierId(carrierId);
    setActiveView("carrier");
    setRefreshKey((prev) => prev + 1);
  }, []);

  /**
   * Carrier view phase changes go through the global phase setter.
   */
  const handleDetailPhaseChange = useCallback(
    (newPhaseId) => {
      setGlobalPhaseId(newPhaseId);
    },
    [setGlobalPhaseId]
  );

  /**
   * Lane view phase changes also go through the exact same global phase setter.
   *
   * Important:
   * - there is no second "parallel phase state" for lane views;
   * - changing the phase here updates the page-wide selectedPhaseId;
   * - this reloads the target-scoped current solution collection from the BE.
   */
  const handleLaneViewPhaseChange = useCallback(
    (newPhaseId) => {
      setGlobalPhaseId(newPhaseId);
    },
    [setGlobalPhaseId]
  );

  const setQuoteAccepted = useCallback(
    async ({ quoteId, nextAccepted }) => {
      if (!isAnalysisWorkflow) return;
      if (!quoteId) return;
      if (acceptBusyIdsRef.current.has(quoteId)) return;

      incLoad();

      setAcceptBusyIds((prev) => {
        const next = new Set(prev);
        next.add(quoteId);
        return next;
      });

      try {
        const action = nextAccepted ? "accept" : "unaccept";

        const res = await tenderQuotesUpdateService.postQuery(
          `/${quoteId}/${action}`
        );

        if (isFailurePayload(res)) {
          toastRef.current?.(
            "error",
            "",
            apiMessage(res, tOr("Erro", "Error"))
          );
          return;
        }

        setQuotes((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          const next = list.map((quote) => {
            const id = getQuoteId(quote);
            if (id !== quoteId) return quote;

            return {
              ...quote,
              IsAccepted: nextAccepted,
              isAccepted: nextAccepted,
            };
          });

          setQuotesAccepted(next.filter((quote) => getQuoteAccepted(quote)));
          setQuotesUnaccepted(next.filter((quote) => !getQuoteAccepted(quote)));

          return next;
        });

        setLastSyncAt(Date.now());
        setRefreshKey((prev) => prev + 1);
      } catch (err) {
        toastRef.current?.("error", "", apiMessage(err, tOr("Erro", "Error")));
      } finally {
        setDistinctCarriersReloadKey((prev) => prev + 1);
        decLoad();

        setAcceptBusyIds((prev) => {
          const next = new Set(prev);
          next.delete(quoteId);
          return next;
        });
      }
    },
    [decLoad, incLoad, isAnalysisWorkflow, tOr, tenderQuotesUpdateService]
  );

  const handleFinish = useCallback(() => {
    if (!sessionLine) return;
    setShowFinishConfirm(true);
  }, [sessionLine]);

  const confirmFinishTender = useCallback(() => {
    setShowFinishConfirm(false);

    if (!sessionLine?.Id) return;

    incLoad();

    tenderServiceUpdate
      .update(sessionLine.Id, { state: 3 })
      .then((res) => {
        if (isFailurePayload(res)) {
          toastRef.current?.(
            "error",
            "",
            apiMessage(res, tOr("ErrorSaving", "Error saving data"))
          );
          return;
        }

        toastRef.current?.(
          "success",
          "",
          tOr("TenderFinished", "Tender finished")
        );
        handleTabClose?.(id);
      })
      .catch((err) =>
        toastRef.current?.(
          "error",
          "",
          apiMessage(err, tOr("ErrorSaving", "Error saving data"))
        )
      )
      .finally(() => {
        decLoad();
      });
  }, [
    incLoad,
    decLoad,
    tenderServiceUpdate,
    sessionLine?.Id,
    tOr,
    handleTabClose,
    id,
  ]);

  const handleMoveToAnalysis = useCallback(() => {
    if (!sessionLine?.Id) return;

    if (!isSpotTender && currentAnalysisTarget.targetEntityId == null) {
      toastRef.current?.(
        "error",
        "",
        tOr(
          "SelectPhaseBeforeMoveToAnalysis",
          "Select a phase before moving to analysis."
        )
      );
      return;
    }

    setShowMoveToAnalysisConfirm(true);
  }, [sessionLine?.Id, isSpotTender, currentAnalysisTarget.targetEntityId, tOr]);

  const confirmMoveToAnalysis = useCallback(() => {
    setShowMoveToAnalysisConfirm(false);

    if (currentAnalysisTarget.targetEntityId == null) return;

    incLoad();

    const request = isSpotTender
      ? tenderServiceUpdate.update(currentAnalysisTarget.targetEntityId, {
        state: 2,
      })
      : tenderPhasesUpdateService.update(currentAnalysisTarget.targetEntityId, {
        state: 2,
      });

    request
      .then((res) => {
        if (isFailurePayload(res)) {
          toastRef.current?.(
            "error",
            "",
            apiMessage(res, tOr("ErrorSaving", "Error saving data"))
          );
          return;
        }

        toastRef.current?.(
          "success",
          "",
          tOr("MovedToAnalysis", "Moved to analysis successfully.")
        );

        handleTabClose?.(id);
      })
      .catch((err) =>
        toastRef.current?.(
          "error",
          "",
          apiMessage(err, tOr("ErrorSaving", "Error saving data"))
        )
      )
      .finally(() => {
        decLoad();
      });
  }, [
    currentAnalysisTarget.targetEntityId,
    incLoad,
    isSpotTender,
    tenderServiceUpdate,
    tenderPhasesUpdateService,
    tOr,
    handleTabClose,
    id,
    decLoad,
  ]);

  const confirmCreatePhase = useCallback(() => {
    if (!sessionLine?.Id) return;

    setShowCreatePhaseConfirm(false);
    incLoad();

    try {
      let lastPhase = null;

      if (phases.length === 1) {
        lastPhase = phases[0];
      } else if (phases.length > 1) {
        const lastIds = phases
          .map((p) => p.LastTenderPhaseId)
          .filter((pid) => pid !== null);

        lastPhase = phases.find((p) => !lastIds.includes(p.Id)) || null;
      }

      navigate("/home/create-tenders", {
        state: {
          isCreatePhase: true,
          tenderInfo,
          lastPhase,
          phases,
          quotes: hvQuotesAll,
          lanes,
          carriers,
          distinctCarriersItems,
        },
      });
    } catch {
      toastRef.current?.(
        "error",
        "",
        tOr(
          "ErrorFinishingLastPhase",
          "Error preparing the new phase flow."
        )
      );
    } finally {
      decLoad();
    }
  }, [
    sessionLine?.Id,
    incLoad,
    phases,
    navigate,
    tenderInfo,
    hvQuotesAll,
    lanes,
    carriers,
    distinctCarriersItems,
    tOr,
    decLoad,
  ]);

  /**
   * Opens the external publication confirmation dialog after validating the current target.
   *
   * @returns {void}
   */
  const handleExternalPublish = useCallback(() => {
    if (!isQuotationWorkflow) return;

    if (!isCurrentExternalPublicationDecisionTarget) {
      toastRef.current?.(
        "error",
        "",
        tOr(
          "ExternalPublicationOnlyAllowedForLastPhase",
          "External publication is only available for the latest tender phase."
        )
      );
      return;
    }

    if (currentExternalPublicationTarget.targetEntityId == null) {
      toastRef.current?.(
        "error",
        "",
        tOr(
          "SelectPhaseBeforeExternalPublication",
          "Select a phase before publishing externally."
        )
      );
      return;
    }

    setShowExternalPublishConfirm(true);
  }, [
    isQuotationWorkflow,
    isCurrentExternalPublicationDecisionTarget,
    currentExternalPublicationTarget.targetEntityId,
    tOr,
  ]);

  /**
   * Publishes the current Tender or TenderPhase to the external provider.
   *
   * @returns {Promise<void>}
   */
  const confirmExternalPublish = useCallback(async () => {
    setShowExternalPublishConfirm(false);

    if (!isCurrentExternalPublicationDecisionTarget) return;
    if (currentExternalPublicationTarget.targetEntityId == null) return;

    const service = isSpotTender ? tenderService : phasesService;
    const segment = buildExternalPublicationSegment(
      currentExternalPublicationTarget.targetEntityId
    );

    incLoad();

    try {
      const res = await service.postQuery(segment);

      if (isFailurePayload(res)) {
        toastRef.current?.(
          "error",
          "",
          apiMessage(res, tOr("ErrorPublishingExternally", "Error publishing externally."))
        );
        return;
      }

      if (res && typeof res === "object") {
        setExternalPublication(res);
        setExternalPublicationLoaded(true);
      }

      setExternalPublicationReloadKey((prev) => prev + 1);

      toastRef.current?.(
        "success",
        "",
        tOr(
          "ExternalPublicationRequested",
          "External publication was requested successfully."
        )
      );
    } catch (err) {
      toastRef.current?.(
        "error",
        "",
        apiMessage(err, tOr("ErrorPublishingExternally", "Error publishing externally."))
      );
    } finally {
      decLoad();
    }
  }, [
    currentExternalPublicationTarget.targetEntityId,
    isCurrentExternalPublicationDecisionTarget,
    isSpotTender,
    tenderService,
    phasesService,
    incLoad,
    decLoad,
    tOr,
  ]);

  //endregion

  //region Derived collections for carrier and solution selectors

  const carriersWithQuotes = useMemo(() => {
    if (!Array.isArray(distinctCarriersItems) || !distinctCarriersItems.length) {
      return [];
    }

    return distinctCarriersItems
      .map((row) => {
        const carrier = row?.Carrier ?? row?.carrier ?? row ?? {};
        const carrierId = carrier?.CarrierId ?? carrier?.Id ?? null;

        return {
          CarrierId: carrierId,
          CarrierName: carrier?.CarrierName ?? carrier?.Name ?? "",
          Name: carrier?.CarrierName ?? carrier?.Name ?? "",
          ...carrier,
          QuotesCount: row?.QuotesCount ?? row?.quotesCount ?? null,
          QuotesAcceptedCount:
            row?.QuotesAcceptedCount ?? row?.quotesAcceptedCount ?? null,
        };
      })
      .filter((carrier) => carrier.CarrierId != null);
  }, [distinctCarriersItems]);

  const carrierSelectorItems = useMemo(() => {
    if (!Array.isArray(carriers) || carriers.length === 0) return [];
    if (isSpotTender) return carriers;

    return carriers.filter((carrier) =>
      isCarrierInvitedInPhase(carrier, selectedPhaseId)
    );
  }, [carriers, isSpotTender, selectedPhaseId]);

  const carrierOptions = useMemo(
    () => carrierSelectorItems.map(buildCarrierSelectOption).filter(Boolean),
    [carrierSelectorItems]
  );

  useEffect(() => {
    if (activeView !== "carrier") return;
    if (selectedCarrierId != null) return;
    if (!Array.isArray(carrierSelectorItems) || carrierSelectorItems.length === 0) {
      return;
    }

    const firstId =
      carrierSelectorItems[0]?.CarrierId ?? carrierSelectorItems[0]?.Id;

    if (firstId != null) setSelectedCarrierId(firstId);
  }, [activeView, selectedCarrierId, carrierSelectorItems]);

  //endregion

  //region Phase-indexed quotes for carrier view

  /**
   * Carrier detail remains current-target scoped.
   *
   * Therefore in Non-Spot mode we index the current full quote set by phase and
   * then pick only the currently selected phase subset for that view.
   */
  const quotesByPhaseId = useMemo(() => {
    const map = new Map();
    if (isSpotTender) return map;

    for (const quote of hvQuotesAllEnriched || []) {
      const pid = getQuotePhaseId(quote);
      if (pid == null) continue;

      if (!map.has(pid)) map.set(pid, []);
      map.get(pid).push(quote);
    }

    return map;
  }, [hvQuotesAllEnriched, isSpotTender]);

  const detailQuotes = useMemo(() => {
    if (isSpotTender) return hvQuotesAllEnriched;
    if (selectedPhaseId == null) return [];
    return quotesByPhaseId.get(selectedPhaseId) || [];
  }, [hvQuotesAllEnriched, isSpotTender, selectedPhaseId, quotesByPhaseId]);

  //endregion

  //region Metric selectors and labels

  const bestMetricOptions = useMemo(
    () => [
      { label: tOr("BidUnitValue", "Bid Unit Value"), value: "unit" },
      { label: tOr("BidValue", "Bid Value"), value: "bid" },
      { label: tOr("Loads", "Loads"), value: "loads" },
      {
        label: tOr("EstimatedTransitDays", "Estimated Transit Days"),
        value: "etd",
      },
    ],
    [tOr]
  );

  const bestMetricValue = useMemo(
    () =>
      bestMetricOptions.find((option) => option.value === bestMetric) ||
      bestMetricOptions[0],
    [bestMetric, bestMetricOptions]
  );

  /**
 * ViewLaneTable uses "transit" internally instead of "etd".
 *
 * This helper keeps the page state semantic ("etd") and adapts it to the table contract.
 */
  const laneTableMetricKey = useMemo(() => {
    switch (bestMetric) {
      case "unit":
        return "unit";
      case "bid":
        return "bid";
      case "loads":
        return "loads";
      case "etd":
        return "transit";
      default:
        return "unit";
    }
  }, [bestMetric]);
  //endregion

  //region Render

  return (
    <>
      <MHeader
        element={
          <>
            {/* ------------------------------------------------------------------ */}
            {/* Main navigation buttons                                              */}
            {/* ------------------------------------------------------------------ */}
            <MButton
              tooltip={tOr("InQuotation", "In Quotation")}
              icon={
                <i
                  className="pi pi-chart-line iconTable"
                  style={{ color: "var(--color-green-light)" }}
                />
              }
              onClick={() => setActiveView("inQuotation")}
              label={tOr("InQuotation", "In Quotation")}
            />

            <MButton
              tooltip={tOr("Carrier", "Carrier")}
              icon={
                <i
                  className="pi pi-truck"
                  style={{ color: "var(--color-green-light)" }}
                />
              }
              onClick={() => setActiveView("carrier")}
              label={tOr("ViewCarriers", "View Carriers")}
            />

            {/* ------------------------------------------------------------------ */}
            {/* Lane views must be accessible in BOTH workflows:                    */}
            {/* - analysis                                                          */}
            {/* - quotation                                                         */}
            {/*                                                                     */}
            {/* In quotation mode, the tables still render, but the child receives  */}
            {/* showAcceptedColumn/showSuggestedColumn = false through              */}
            {/* isAnalysisWorkflow.                                                 */}
            {/* ------------------------------------------------------------------ */}
            <MButton
              tooltip={tOr("AcceptedByLane", "Accepted By Lane")}
              icon={
                <i
                  className="pi pi-check-square iconTable"
                  style={{ color: "var(--color-green-light)" }}
                />
              }
              onClick={() => setActiveView("acceptedByLane")}
              label={tOr("AcceptedByLane", "Accepted By Lane")}
            />

            <MButton
              tooltip={tOr("TotalByLane", "Total By Lane")}
              icon={
                <i
                  className="pi pi-table iconTable"
                  style={{ color: "var(--color-green-light)" }}
                />
              }
              onClick={() => setActiveView("totalByLane")}
              label={tOr("TotalByLane", "Total By Lane")}
            />

            {/* ------------------------------------------------------------------ */}
            {/* Summary remains analysis-only                                       */}
            {/* ------------------------------------------------------------------ */}
            {isAnalysisWorkflow && (
              <MButton
                tooltip={tOr("Summary", "Summary")}
                icon={
                  <i
                    className="pi pi-chart-bar iconTable"
                    style={{ color: "var(--color-green-light)" }}
                  />
                }
                onClick={() => setActiveView("summary")}
                label={tOr("ViewSummary", "Summary")}
              />
            )}

            {/* ------------------------------------------------------------------ */}
            {/* Quotation-only OpenSyllo actions                                     */}
            {/* ------------------------------------------------------------------ */}
            {shouldShowExternalPublishButton && (
              <MButton
                tooltip={tOr("PublishExternally", "Publish externally")}
                icon={
                  <i
                    className="pi pi-cloud-upload iconTable"
                    style={{ color: "var(--color-blue)" }}
                  />
                }
                onClick={handleExternalPublish}
                label={tOr("PublishExternally", "Publish externally")}
              />
            )}

            {shouldShowCarrierInterestsButton && (
              <MButton
                tooltip={tOr("CarrierInterests", "Carrier Interests")}
                icon={
                  <i
                    className="pi pi-users iconTable"
                    style={{ color: "var(--color-blue)" }}
                  />
                }
                onClick={() => setActiveView("carrierInterests")}
                label={tOr("CarrierInterests", "Carrier Interests")}
              />
            )}

            {/* ------------------------------------------------------------------ */}
            {/* Analysis-only actions                                               */}
            {/* ------------------------------------------------------------------ */}
            {isAnalysisWorkflow && !isSpotTender && !isTenderFinished && (
              <MButton
                tooltip={tOr("CreateNewTenderPhase", "Create New Tender Phase")}
                icon={
                  <i
                    className="pi pi-plus-circle iconTable"
                    style={{ color: "var(--color-red)" }}
                  />
                }
                onClick={() => setShowCreatePhaseConfirm(true)}
                label={tOr("CreateNewTenderPhase", "Create New Tender Phase")}
              />
            )}

            {isAnalysisWorkflow && !isTenderFinished && (
              <MButton
                tooltip={tOr("FinishTender", "Finish Tender")}
                icon={
                  <i
                    className="pi pi-check iconTable"
                    style={{ color: "var(--color-red)" }}
                  />
                }
                onClick={handleFinish}
                label={tOr("FinishTender", "Finish Tender")}
              />
            )}

            {/* ------------------------------------------------------------------ */}
            {/* Quotation-only action                                               */}
            {/* ------------------------------------------------------------------ */}
            {isQuotationWorkflow && !isTenderFinished && (
              <MButton
                tooltip={tOr("MoveToAnalysis", "Move To Analysis")}
                icon={
                  <i
                    className="pi pi-bullseye iconTable"
                    style={{ color: "var(--color-red)" }}
                  />
                }
                onClick={handleMoveToAnalysis}
                label={tOr("MoveToAnalysis", "Move To Analysis")}
              />
            )}
          </>
        }
      />

      {/* ---------------------------------------------------------------------- */}
      {/* Analysis status banner remains analysis-only                            */}
      {/* ---------------------------------------------------------------------- */}
      {isAnalysisWorkflow && (
        <div
          className={`tiq-analysis-status-banner tiq-analysis-status-banner--page ${getAnalysisBannerToneClass(
            analysisStatusInfo.tone
          )}`}
        >
          <div className="tiq-analysis-status-banner-title">
            {analysisStatusInfo.title}
          </div>

          <div className="tiq-analysis-status-banner-text">
            {analysisStatusInfo.message}
          </div>
        </div>
      )}

      {/* ---------------------------------------------------------------------- */}
      {/* Confirm dialogs                                                         */}
      {/* ---------------------------------------------------------------------- */}
      <MMessageBoard
        title={tOr("ConfirmFinishTender", "Confirm Finish Tender")}
        open={showFinishConfirm}
        onClose={() => setShowFinishConfirm(false)}
        warning
        body={
          <div className="tiq-message-body">
            {tOr(
              "AreYouSureYouWantToFinishTender",
              "Are you sure you want to finish this Tender?"
            )}
          </div>
        }
        footer={
          <>
            <MButton
              label={tOr("Cancel", "Cancel")}
              backgroundColor={"var(--color-red)"}
              labelColor={"var(--color-white)"}
              icon={
                <i
                  className="pi pi-times iconTable"
                  style={{ color: "white" }}
                />
              }
              onClick={() => setShowFinishConfirm(false)}
            />
            <MButton
              label={tOr("Confirm", "Confirm")}
              onClick={confirmFinishTender}
              backgroundColor={"var(--color-green)"}
              labelColor={"var(--color-white)"}
              icon={
                <i
                  className="pi pi-check iconTable"
                  style={{ color: "white" }}
                />
              }
            />
          </>
        }
      />

      <MMessageBoard
        title={tOr("ConfirmMoveToAnalysis", "Confirm Move To Analysis")}
        open={showMoveToAnalysisConfirm}
        onClose={() => setShowMoveToAnalysisConfirm(false)}
        warning
        body={
          <div className="tiq-message-body">
            {tOr(
              "AreYouSureYouWantToMoveToAnalysis",
              "Are you sure you want to move the current target to analysis?"
            )}
          </div>
        }
        footer={
          <>
            <MButton
              label={tOr("Cancel", "Cancel")}
              backgroundColor={"var(--color-red)"}
              labelColor={"var(--color-white)"}
              icon={
                <i
                  className="pi pi-times iconTable"
                  style={{ color: "white" }}
                />
              }
              onClick={() => setShowMoveToAnalysisConfirm(false)}
            />
            <MButton
              label={tOr("Confirm", "Confirm")}
              onClick={confirmMoveToAnalysis}
              backgroundColor={"var(--color-green)"}
              labelColor={"var(--color-white)"}
              icon={
                <i
                  className="pi pi-check iconTable"
                  style={{ color: "white" }}
                />
              }
            />
          </>
        }
      />

      <MMessageBoard
        title={tOr("ConfirmCreateNewPhase", "Confirm Create New Phase")}
        open={showCreatePhaseConfirm}
        onClose={() => setShowCreatePhaseConfirm(false)}
        warning
        body={
          <div className="tiq-message-body">
            {tOr(
              "AreYouSureYouWantToCreateNewPhase",
              "Are you sure you want to create a new TenderPhase?"
            )}
          </div>
        }
        footer={
          <>
            <MButton
              label={tOr("Cancel", "Cancel")}
              backgroundColor={"var(--color-red)"}
              labelColor={"var(--color-white)"}
              icon={
                <i
                  className="pi pi-times iconTable"
                  style={{ color: "white" }}
                />
              }
              onClick={() => setShowCreatePhaseConfirm(false)}
            />
            <MButton
              label={tOr("Confirm", "Confirm")}
              onClick={confirmCreatePhase}
              backgroundColor={"var(--color-green)"}
              labelColor={"var(--color-white)"}
              icon={
                <i
                  className="pi pi-check iconTable"
                  style={{ color: "white" }}
                />
              }
            />
          </>
        }
      />

      <MMessageBoard
        title={tOr("ConfirmExternalPublication", "Confirm external publication")}
        open={showExternalPublishConfirm}
        onClose={() => setShowExternalPublishConfirm(false)}
        warning
        body={
          <div className="tiq-message-body">
            {tOr(
              "AreYouSureYouWantToPublishExternally",
              "This action will publish the current Tender or TenderPhase to an external source outside the Shipperform environment. Do you want to continue?"
            )}
          </div>
        }
        footer={
          <>
            <MButton
              label={tOr("Cancel", "Cancel")}
              backgroundColor={"var(--color-red)"}
              labelColor={"var(--color-white)"}
              icon={
                <i
                  className="pi pi-times iconTable"
                  style={{ color: "white" }}
                />
              }
              onClick={() => setShowExternalPublishConfirm(false)}
            />
            <MButton
              label={tOr("Confirm", "Confirm")}
              onClick={confirmExternalPublish}
              backgroundColor={"var(--color-green)"}
              labelColor={"var(--color-white)"}
              icon={
                <i
                  className="pi pi-check iconTable"
                  style={{ color: "white" }}
                />
              }
            />
          </>
        }
      />

      <div className="tiq-page-root">
        {/* -------------------------------------------------------------------- */}
        {/* InQuotation overview                                                  */}
        {/* -------------------------------------------------------------------- */}
        {activeView === "inQuotation" && (
          <div className="firstDivContainer tiq-main-view">
            <div className="tiq-form-card">
              <InQuotationForm
                tenderInfo={hvTender}
                phases={hvPhases}
                setTenderInfo={() => { }}
                isSpotTender={isSpotTender}
                selectedPhaseId={selectedPhaseId}
                setSelectedPhaseId={setGlobalPhaseId}
                tenderSchemaOptions={tenderSchemaOptions}
              />
            </div>

            <div className="tiq-panels-row">
              <div className="tiq-panel-card">
                <CarrierResponsesTable
                  tenderId={hvTender?.Id}
                  phases={hvPhases}
                  responses={hvQuotesAll}
                  carriers={carriers}
                  isSpotTender={isSpotTender}
                  setActiveView={setActiveView}
                  setSelectedCarrierId={setSelectedCarrierId}
                  selectedPhaseId={selectedPhaseId}
                  setSelectedPhaseId={setGlobalPhaseId}
                />
              </div>

              <div className="tiq-panel-card">
                <TenderQuotesAnalysis
                  tenderId={hvTender?.Id}
                  routeId={hvTender?.RouteId}
                  isSpotTender={isSpotTender}
                  currency={hvTender?.Currency ?? hvTender?.currency ?? ""}
                  lanes={hvLanes}
                  laneSummaries={hvLaneSummaries}
                  quotes={currentTargetQuotes}
                  workflowMode={workflowMode}
                  analysisRun={currentAnalysisRun}
                  analysisSolutionReady={isAnalysisSolutionReady}
                />
              </div>
            </div>
          </div>
        )}

        {/* -------------------------------------------------------------------- */}
        {/* Carrier detail view                                                   */}
        {/* -------------------------------------------------------------------- */}
        {activeView === "carrier" && (
          <div className="tiq-carrier-detail-card">
            <span className="tiq-carrier-detail-title">
              {tOr("CarrierResponses", "Carrier Responses")}
            </span>

            <div className="tiq-scroll-x">
              <div className="tiq-scroll-inner">
                <div className="tiq-carrier-detail-filters">
                  {!isSpotTender && (
                    <MSelect
                      label={tOr("Phase", "Phase")}
                      value={selectedPhaseOption}
                      onChangeValue={(newValue) => {
                        if (newValue && typeof newValue === "object") {
                          handleDetailPhaseChange(newValue.value);
                        } else {
                          handleDetailPhaseChange(null);
                        }
                      }}
                      options={phaseOptions}
                      autocomplete
                      required
                    />
                  )}

                  <MSelect
                    label={tOr("Carrier", "Carrier")}
                    value={
                      carrierOptions.find(
                        (option) =>
                          String(option.value) === String(selectedCarrierId ?? "")
                      ) || null
                    }
                    onChangeValue={(newValue) => {
                      if (newValue && typeof newValue === "object") {
                        setSelectedCarrierId(newValue.value);
                      } else {
                        setSelectedCarrierId(null);
                      }
                    }}
                    options={carrierOptions}
                    autocomplete
                    required
                  />

                  <MSelect
                    label={tOr("BestMetric", "Best Metric")}
                    value={bestMetricValue}
                    onChangeValue={(newValue) => {
                      if (newValue && typeof newValue === "object") {
                        setBestMetric(newValue.value);
                      } else {
                        setBestMetric("unit");
                      }
                    }}
                    options={bestMetricOptions}
                    autocomplete
                    required
                  />

                  {isAnalysisWorkflow &&
                    isAnalysisSolutionReady &&
                    solutionOptions.length > 0 && (
                      <MSelect
                        label={tOr("Solution", "Solution")}
                        value={selectedSolutionOption}
                        onChangeValue={(newValue) => {
                          if (newValue && typeof newValue === "object") {
                            setSelectedSolutionId(newValue.value);
                          } else {
                            setSelectedSolutionId(null);
                          }
                        }}
                        options={solutionOptions}
                        autocomplete
                        required
                      />
                    )}
                </div>
              </div>
            </div>

            {shouldRenderSelectedSolutionMetricsBanner && (
              <div className="tiq-analysis-status-banner tiq-analysis-status-banner--inline tiq-analysis-status-banner--success">
                <div className="tiq-analysis-status-banner-title">
                  {tOr("SolutionSuggestionSummary", "Solution suggestion summary")}
                </div>

                <div className="tiq-analysis-status-banner-text">
                  {selectedSolutionMetricsBannerMessage}
                </div>
              </div>
            )}

            <div className="tiq-scroll-x tiq-carrier-detail-table-wrapper">
              <div className="tiq-scroll-inner">
                <CarrierResponseDetailTable
                  tenderInfo={hvTender}
                  selectedCarrier={selectedCarrierId}
                  selectedPhaseId={isSpotTender ? null : selectedPhaseId}
                  selectedSolutionId={selectedSolutionId}
                  selectedSolution={selectedSolution}
                  selectedSolutionAwards={selectedSolutionAwardsForCarrier}
                  selectedSolutionSuggestedQuoteIds={selectedSolutionAwardsForCarrier
                    .map((award) => getAwardQuoteId(award))
                    .filter((quoteId) => quoteId != null)}
                  quotes={detailQuotes}
                  lanes={hvLanes}
                  isSpotTender={isSpotTender}
                  bestMetric={bestMetric}
                  laneBestMap={currentTargetLaneBestMap}
                  acceptBusyIds={acceptBusyIds}
                  onSetQuoteAccepted={setQuoteAccepted}
                  showAcceptedColumn={isAnalysisWorkflow}
                  showSuggestedColumn={isAnalysisWorkflow}
                  analysisRun={currentAnalysisRun}
                  analysisSolutionReady={isAnalysisSolutionReady}
                  workflowMode={workflowMode}
                  onRefresh={() => refreshCarrierDetailTable(selectedCarrierId)}
                />
              </div>
            </div>
          </div>
        )}

        {/* -------------------------------------------------------------------- */}
        {/* Accepted By Lane                                                     */}
        {/* Available in BOTH workflows.                                         */}
        {/* In quotation mode, the table still renders but without Accepted /     */}
        {/* Suggested columns because those props resolve to false.               */}
        {/* -------------------------------------------------------------------- */}
        {activeView === "acceptedByLane" && (
          <div className="tiq-carrier-detail-card">
            <span className="tiq-carrier-detail-title">
              {tOr("AcceptedByLane", "Accepted By Lane")}
            </span>

            <div className="tiq-scroll-x">
              <div className="tiq-scroll-inner">
                <div className="tiq-carrier-detail-filters">
                  {!isSpotTender && (
                    <MSelect
                      label={tOr("Phase", "Phase")}
                      value={selectedPhaseOption}
                      onChangeValue={(newValue) => {
                        if (newValue && typeof newValue === "object") {
                          handleLaneViewPhaseChange(newValue.value);
                        } else {
                          handleLaneViewPhaseChange(null);
                        }
                      }}
                      options={phaseOptions}
                      autocomplete
                      required
                    />
                  )}

                  <MSelect
                    label={tOr("BestMetric", "Best Metric")}
                    value={bestMetricValue}
                    onChangeValue={(newValue) => {
                      if (newValue && typeof newValue === "object") {
                        setBestMetric(newValue.value);
                      } else {
                        setBestMetric("unit");
                      }
                    }}
                    options={bestMetricOptions}
                    autocomplete
                    required
                  />

                  {isAnalysisWorkflow &&
                    isAnalysisSolutionReady &&
                    solutionOptions.length > 0 && (
                      <MSelect
                        label={tOr("Solution", "Solution")}
                        value={selectedSolutionOption}
                        onChangeValue={(newValue) => {
                          if (newValue && typeof newValue === "object") {
                            setSelectedSolutionId(newValue.value);
                          } else {
                            setSelectedSolutionId(null);
                          }
                        }}
                        options={solutionOptions}
                        autocomplete
                        required
                      />
                    )}
                </div>
              </div>
            </div>

            {shouldRenderSelectedSolutionMetricsBanner && (
              <div className="tiq-analysis-status-banner tiq-analysis-status-banner--inline tiq-analysis-status-banner--success">
                <div className="tiq-analysis-status-banner-title">
                  {tOr("SolutionSuggestionSummary", "Solution suggestion summary")}
                </div>

                <div className="tiq-analysis-status-banner-text">
                  {selectedSolutionMetricsBannerMessage}
                </div>
              </div>
            )}

            <div className="tiq-scroll-x tiq-carrier-detail-table-wrapper">
              <div className="tiq-scroll-inner">
                <ViewLaneTable
                  tenderInfo={hvTender}
                  acceptedQuotes={hvQuotesAcceptedEnriched}
                  allQuotes={hvQuotesAllEnriched}
                  lanes={hvAllLanes}
                  carriers={carriersWithQuotes}
                  onRefresh={() => setRefreshKey((prev) => prev + 1)}
                  metricKey={laneTableMetricKey}
                  laneBestMap={acceptedByLaneBestMap}
                  laneRemainingLoadsByLaneId={acceptedByLaneRemainingLoadsMap}
                  tenderTargetLoads={tenderLoads}
                  laneMode={"accepted"}
                  isFinished={isTenderFinished}
                  isSpotTender={isSpotTender}
                  /*
                   * Context only.
                   * ViewLaneTable must not phase-filter visible quotes with this prop.
                   * In analysis it helps define the solution target context.
                   * In quotation it is harmless context and the table remains Tender-wide.
                   */
                  currentPhaseId={selectedPhaseId ?? lastPhaseId}
                  acceptBusyIds={acceptBusyIds}
                  onSetQuoteAccepted={setQuoteAccepted}
                  showSuggestedColumn={isAnalysisWorkflow}
                  showAcceptedColumn={isAnalysisWorkflow}
                  analysisRun={currentAnalysisRun}
                  analysisSolutionReady={isAnalysisSolutionReady}
                  selectedSolutionId={selectedSolutionId}
                  selectedSolution={selectedSolution}
                  selectedSolutionAwards={selectedSolutionAwardsForCurrentTarget}
                  selectedSolutionSuggestedQuoteIds={selectedSolutionSuggestedQuoteIds}
                  selectedSolutionAwardsByLaneId={selectedSolutionAwardsByLaneId}
                  workflowMode={workflowMode}
                />
              </div>
            </div>
          </div>
        )}

        {/* -------------------------------------------------------------------- */}
        {/* Total By Lane                                                        */}
        {/* Available in BOTH workflows.                                         */}
        {/* In quotation mode, the table still renders but without Accepted /     */}
        {/* Suggested columns because those props resolve to false.               */}
        {/* -------------------------------------------------------------------- */}
        {activeView === "totalByLane" && (
          <div className="tiq-carrier-detail-card">
            <span className="tiq-carrier-detail-title">
              {tOr("TotalByLane", "Total By Lane")}
            </span>

            <div className="tiq-scroll-x">
              <div className="tiq-scroll-inner">
                <div className="tiq-carrier-detail-filters">
                  {!isSpotTender && (
                    <MSelect
                      label={tOr("Phase", "Phase")}
                      value={selectedPhaseOption}
                      onChangeValue={(newValue) => {
                        if (newValue && typeof newValue === "object") {
                          handleLaneViewPhaseChange(newValue.value);
                        } else {
                          handleLaneViewPhaseChange(null);
                        }
                      }}
                      options={phaseOptions}
                      autocomplete
                      required
                    />
                  )}

                  <MSelect
                    label={tOr("BestMetric", "Best Metric")}
                    value={bestMetricValue}
                    onChangeValue={(newValue) => {
                      if (newValue && typeof newValue === "object") {
                        setBestMetric(newValue.value);
                      } else {
                        setBestMetric("unit");
                      }
                    }}
                    options={bestMetricOptions}
                    autocomplete
                    required
                  />

                  {isAnalysisWorkflow &&
                    isAnalysisSolutionReady &&
                    solutionOptions.length > 0 && (
                      <MSelect
                        label={tOr("Solution", "Solution")}
                        value={selectedSolutionOption}
                        onChangeValue={(newValue) => {
                          if (newValue && typeof newValue === "object") {
                            setSelectedSolutionId(newValue.value);
                          } else {
                            setSelectedSolutionId(null);
                          }
                        }}
                        options={solutionOptions}
                        autocomplete
                        required
                      />
                    )}
                </div>
              </div>
            </div>

            {shouldRenderSelectedSolutionMetricsBanner && (
              <div className="tiq-analysis-status-banner tiq-analysis-status-banner--inline tiq-analysis-status-banner--success">
                <div className="tiq-analysis-status-banner-title">
                  {tOr("SolutionSuggestionSummary", "Solution suggestion summary")}
                </div>

                <div className="tiq-analysis-status-banner-text">
                  {selectedSolutionMetricsBannerMessage}
                </div>
              </div>
            )}

            <div className="tiq-scroll-x tiq-carrier-detail-table-wrapper">
              <div className="tiq-scroll-inner">
                <ViewLaneTable
                  tenderInfo={hvTender}
                  acceptedQuotes={hvQuotesAcceptedEnriched}
                  allQuotes={hvQuotesAllEnriched}
                  lanes={hvAllLanes}
                  carriers={carriersWithQuotes}
                  onRefresh={() => setRefreshKey((prev) => prev + 1)}
                  laneMode={"total"}
                  metricKey={laneTableMetricKey}
                  laneBestMap={totalByLaneBestMap}
                  laneRemainingLoadsByLaneId={totalByLaneRemainingLoadsMap}
                  tenderTargetLoads={tenderLoads}
                  isFinished={isTenderFinished}
                  isSpotTender={isSpotTender}
                  /*
                   * Context only.
                   * ViewLaneTable must not phase-filter visible quotes with this prop.
                   * In analysis it helps define the solution target context.
                   * In quotation it is harmless context and the table remains Tender-wide.
                   */
                  currentPhaseId={selectedPhaseId ?? lastPhaseId}
                  acceptBusyIds={acceptBusyIds}
                  onSetQuoteAccepted={setQuoteAccepted}
                  showSuggestedColumn={isAnalysisWorkflow}
                  showAcceptedColumn={isAnalysisWorkflow}
                  analysisRun={currentAnalysisRun}
                  analysisSolutionReady={isAnalysisSolutionReady}
                  selectedSolutionId={selectedSolutionId}
                  selectedSolution={selectedSolution}
                  selectedSolutionAwards={selectedSolutionAwardsForCurrentTarget}
                  selectedSolutionSuggestedQuoteIds={selectedSolutionSuggestedQuoteIds}
                  selectedSolutionAwardsByLaneId={selectedSolutionAwardsByLaneId}
                  workflowMode={workflowMode}
                />
              </div>
            </div>
          </div>
        )}

        {/* -------------------------------------------------------------------- */}
        {/* Summary remains analysis-only                                        */}
        {/* -------------------------------------------------------------------- */}
        {isAnalysisWorkflow && activeView === "summary" && (
          <div className="tiq-carrier-detail-card">
            <span className="tiq-carrier-detail-title">
              {tOr("Summary", "Summary")}
            </span>

            <div className="tiq-scroll-x tiq-carrier-detail-table-wrapper">
              <div className="tiq-scroll-inner">
                <TenderSummaryTable
                  tenderInfo={hvTender}
                  isSpotTender={isSpotTender}
                  rows={summaryLaneRows}
                  onRefresh={() => setRefreshKey((prev) => prev + 1)}
                  workflowMode={workflowMode}
                  analysisRun={currentAnalysisRun}
                  analysisSolutionReady={isAnalysisSolutionReady}
                />
              </div>
            </div>
          </div>
        )}

        {/* -------------------------------------------------------------------- */}
        {/* Carrier Interests placeholder                                        */}
        {/* -------------------------------------------------------------------- */}
        {activeView === "carrierInterests" && activeExternalPublication && (
          <CarrierInterestsConfig
            publication={activeExternalPublication}
            tenderInfo={hvTender}
            isSpotTender={isSpotTender}
            selectedPhaseId={selectedPhaseId ?? lastPhaseId}
          />
        )}
      </div>
    </>
  );
}

//endregion

TenderOpenPage.propTypes = {
  handleTabClose: PropTypes.func,
  toastMessages: PropTypes.func,
  workflowMode: PropTypes.oneOf(["quotation", "analysis"]),
};

//endregion